import React, { useEffect, useState, useMemo } from "react";
import { db, auth, storage } from "../../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  Timestamp,
  runTransaction,
  onSnapshot,
} from "firebase/firestore";
import { useBuilding } from "../../context/BuildingContext";
import { useToast } from "../../components/Toast";
import { isDemoUser, DEMO_BLOCK_MESSAGE } from "../../utils/demoMode";
import { formatCurrencyUY, formatDateUY } from "../../utils/formatters";
import { tolerantId, fetchUsersByUserIds } from "../../utils/firestoreHelpers";
import { sobrepagoAcreditable } from "../../utils/creditApplication";
import { roundPesos, saldoEnPesos } from "../../utils/redondeoPesos";
import { esUnidadDelEdificio, esAlquilerDelEdificio, esPagoDeAlquiler } from "../../utils/unidadesEdificio";
import { consultaEntregaWhatsApp } from "../../utils/consultasEdificio";
import { logAudit } from "../../utils/auditLog";
import { friendlyBreakdownName } from "../../utils/fundHelpers";
import {
  FaMoneyBillWave,
  FaBuilding,
  FaChevronDown,
  FaChevronRight,
  FaChevronUp,
  FaSearch,
  FaCheck,
  FaTimes,
  FaClock,
  FaFileImage,
  FaInfoCircle,
  FaEye,
  FaPercentage,
  FaUpload,
} from "react-icons/fa";
import { colors, shadows, radius, spacing, typography, transitions, cardBase, btnPrimary, btnSecondary, btnDanger, btnGhost, inputBase, pageTitle, modalOverlay } from "../../utils/designTokens";
import { HABI_API_BASE_URL, getAuthHeaders, sendCollectionWhatsApp, sendCollectionMessageCallable } from "../../utils/habiApi";
import { doc as fsDoc, getDoc as fsGetDoc } from "firebase/firestore";
import StatusBadgeShared from "../../components/StatusBadge";
import AdminModal from "../../components/AdminModal";
import LoadingState from "../../components/LoadingState";
import EmptyState from "../../components/EmptyState";
import KPICard from "../../components/KPICard";
import KpiInfoTooltip from "../../components/KpiInfoTooltip";
import { getKpiBadgeColor } from "../../utils/kpiDefinitions";

// StatusBadge local eliminado — se usa StatusBadgeShared del design system

// Fecha de hoy en formato YYYY-MM-DD (hora local) para inputs type="date"
const getTodayInputDate = () => {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10);
};

// Pago manual: el comprobante es opcional (no siempre lo hay); si se adjunta, queda en el pago.
const formPagoManual = (paidAmount = "") => ({ paidAmount, paymentMethod: "transferencia", adminNotes: "", paymentDate: getTodayInputDate(), proof: null, aviso: null });

// Aviso al residente (pago aprobado / parcial / rechazado): lo decide el admin
// en cada acción. La función onPaymentStatusChanged no avisa si en ESA misma
// actualización viene avisoResidente === false (ver functions/utils/helpers.js
// avisoSilenciado). avisoResidenteDecididoEn distingue una decisión nueva de
// una vieja que quedó guardada en el pago.
const camposAviso = (aviso) => ({
  avisoResidente: aviso === true,
  avisoResidenteDecididoEn: Timestamp.now(),
  avisoResidenteDecididoPor: auth.currentUser?.email || "",
});

// Pregunta obligatoria: Sí / No, sin opción elegida por defecto.
const PreguntaAviso = ({ nombre, valor, onChange, disabled, idBase }) => (
  <fieldset style={{ border: "none", margin: 0, padding: 0 }}>
    <legend style={styles.label}>¿Le avisamos a {nombre || "el residente"} por WhatsApp? *</legend>
    <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
      {[{ v: true, t: "Sí, avisarle" }, { v: false, t: "No avisarle" }].map(({ v, t }) => (
        <label key={String(v)} htmlFor={`${idBase}-${v ? "si" : "no"}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: typography.sizes.sm, color: colors.gray900, cursor: disabled ? "default" : "pointer" }}>
          <input type="radio" id={`${idBase}-${v ? "si" : "no"}`} name={idBase} checked={valor === v} onChange={() => onChange(v)} disabled={disabled} />
          {t}
        </label>
      ))}
    </div>
    {valor === false && (
      <span style={{ display: "block", marginTop: 4, fontSize: typography.sizes.xs, color: colors.gray600 }}>
        No le llega ni WhatsApp ni email por este cambio.
      </span>
    )}
  </fieldset>
);
const TIPOS_COMPROBANTE = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
const MAX_COMPROBANTE_MB = 10;

const CobranzaAdmin = () => {
  const { showToast } = useToast();
  const { selectedBuilding } = useBuilding();
  const buildingId = selectedBuilding?.buildingid;

  const [payments, setPayments] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({}); // { unitId: true }
  const [filter, setFilter] = useState("todas");
  const [search, setSearch] = useState("");
  const [verifyModal, setVerifyModal] = useState(null); // payment to verify
  const [verifyPaidInput, setVerifyPaidInput] = useState(""); // R4: monto realmente recibido
  const [avisoVerificacion, setAvisoVerificacion] = useState(null); // ¿avisarle al residente? (true/false, null = sin elegir)
  const [rejectReason, setRejectReason] = useState("");

  // R4: al abrir el modal de verificación, prefijar el monto recibido con lo que
  // el residente declaró (paidAmount) o el total del pago.
  useEffect(() => {
    if (verifyModal) {
      const declared = Number(verifyModal.paidAmount) || Number(verifyModal.amount) || 0;
      setVerifyPaidInput(String(declared));
    } else {
      setVerifyPaidInput("");
    }
  }, [verifyModal]);
  const [showProof, setShowProof] = useState(null); // proof URL

  // A4 — Cobranza inteligente (Fase 2)
  const [showCollectionPlan, setShowCollectionPlan] = useState(false);
  const [collectionPlan, setCollectionPlan] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [planPreview, setPlanPreview] = useState(null); // { userid, message }
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [pendingPreview, setPendingPreview] = useState(null); // { userid, stage } durante la generacion
  const [lastError, setLastError] = useState(null); // { context, message, status, body }
  const [sendingLog, setSendingLog] = useState(null); // { userid, channel }
  const [sentByUserid, setSentByUserid] = useState({}); // { [userid]: { stage, channel, at } }
  // Sprint 3 — info de templates aprobados/habilitados para este edificio.
  // Map { stage: { templateKey, approved, enabledForBuilding } }
  const [templateAvailability, setTemplateAvailability] = useState({});
  // Texto editable del mensaje generado (admin puede ajustar antes de enviar)
  const [editedWhatsapp, setEditedWhatsapp] = useState("");
  const [editedSubject, setEditedSubject] = useState("");
  const [sendingDirect, setSendingDirect] = useState(null); // "whatsapp" | "email" | null

  // C2 — Carta-documento (Fase 3)
  const [legalLetter, setLegalLetter] = useState(null); // { markdown, checklist }
  const [generatingLetter, setGeneratingLetter] = useState(false);

  // Sprint 3 — Pre-flight: cuando abre el modal, chequea qué templates están
  // aprobados por Meta Y habilitados para este edificio. Eso decide si el
  // envío puede ser 100% automático (vía notifyUser con template) o cae al
  // fallback wa.me (admin clickea 1 vez para mandar).
  useEffect(() => {
    if (!showCollectionPlan || !selectedBuilding?.id) return;
    const loadTemplateAvailability = async () => {
      try {
        const tplSnap = await getDocs(
          query(collection(db, "notificationTemplates"), where("category", "==", "cobranza"))
        );
        const approvedByStage = {};
        tplSnap.docs.forEach((d) => {
          const data = d.data();
          if (data.metaStatus === "APPROVED" && data.active !== false && data.stage) {
            approvedByStage[data.stage] = { templateKey: data.key || d.id };
          }
        });
        const buildingDocSnap = await fsGetDoc(fsDoc(db, "building", selectedBuilding.id));
        const overrides = buildingDocSnap.data()?.communicationTemplates || {};
        const result = {};
        for (const stage of Object.keys(approvedByStage)) {
          const { templateKey } = approvedByStage[stage];
          const override = overrides[templateKey];
          result[stage] = {
            templateKey,
            approved: true,
            enabledForBuilding: !!override?.enabled,
          };
        }
        setTemplateAvailability(result);
      } catch (e) {
        console.warn("[A4] No se pudo leer template availability:", e);
        setTemplateAvailability({});
      }
    };
    loadTemplateAvailability();
  }, [showCollectionPlan, selectedBuilding?.id]);

  const fetchCollectionPlan = async () => {
    if (!buildingId) return;
    try {
      setLoadingPlan(true);
      const headers = await getAuthHeaders();
      const resp = await fetch(
        `${HABI_API_BASE_URL}/api/collection-plan/${buildingId}`,
        { headers }
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        showToast(err.detail || "No se pudo cargar el plan de cobranza.", "error");
        return;
      }
      const data = await resp.json();
      setCollectionPlan(data);
    } catch (e) {
      showToast(e.message || "Error inesperado cargando plan.", "error");
    } finally {
      setLoadingPlan(false);
    }
  };

  const fetchCollectionPreview = async (userid, stage) => {
    if (!buildingId || !userid) {
      setLastError({ context: "preview", message: `Falta buildingId (${buildingId}) o userid (${userid}).` });
      return;
    }
    try {
      setLoadingPreview(true);
      setPendingPreview({ userid: String(userid), stage });
      setPlanPreview(null);
      setLastError(null);
      setEditedWhatsapp("");
      setEditedSubject("");
      const headers = await getAuthHeaders();
      const url = `${HABI_API_BASE_URL}/api/collection-plan/preview-message`;
      console.info("[A4] preview-message request", { url, building_id: String(buildingId), userid: String(userid), stage });
      const resp = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({
          building_id: String(buildingId),
          userid: String(userid),
          stage: stage ?? null,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        const detail = err.detail || err.error || err.message;
        const errInfo = { context: "preview", message: detail || `HTTP ${resp.status}`, status: resp.status, body: err };
        setLastError(errInfo);
        const msg = detail
          ? `No se pudo generar el preview: ${detail}`
          : `No se pudo generar el preview (HTTP ${resp.status}). Detalle abajo.`;
        showToast(msg, "error");
        console.error("[A4] preview-message error", errInfo);
        return;
      }
      const data = await resp.json();
      console.info("[A4] preview-message response", data);
      setPlanPreview({ userid: String(userid), ...data });
      setEditedWhatsapp(data?.message?.whatsapp_text || "");
      setEditedSubject(data?.message?.subject || "");
    } catch (e) {
      const errInfo = { context: "preview", message: e.message || String(e) };
      setLastError(errInfo);
      showToast(`Error inesperado generando preview: ${errInfo.message}`, "error");
      console.error("[A4] preview-message exception", e);
    } finally {
      setLoadingPreview(false);
      setPendingPreview(null);
    }
  };

  // Celular del residente, SOLO para armar el link wa.me (el envío por Meta lo
  // resuelve el servidor, contrato K3). La ficha se lee con el edificio fijado
  // (reglas 0D, C10: accessBids array-contains); si hay varias con ese userid,
  // fetchUsersByUserIds devuelve la de residente vigente. null si no hay o falla.
  const resolveResidentPhone = async (userid, bid = buildingId) => {
    try {
      const [ficha] = await fetchUsersByUserIds([userid], bid);
      if (!ficha) return null;
      // Probar varios campos posibles
      return ficha.celular || ficha.phone || ficha.telefono || ficha.whatsapp || null;
    } catch (e) {
      console.warn("[A4] resolveResidentPhone falló:", e?.code || e?.message || e);
      return null;
    }
  };

  // Normaliza a E.164 uruguayo. Si ya viene con +, se respeta.
  const normalizePhoneUY = (raw) => {
    if (!raw) return null;
    let s = String(raw).replace(/[\s\-()]/g, "");
    if (s.startsWith("+")) return s;
    if (s.startsWith("00")) return "+" + s.slice(2);
    if (s.startsWith("598")) return "+" + s;
    if (s.startsWith("09") && s.length === 9) return "+598" + s.slice(1);
    if (s.startsWith("9") && s.length === 8) return "+598" + s;
    return "+598" + s;
  };

  // Construye el link wa.me con mensaje precargado (siempre funciona, no requiere ventana 24h)
  const buildWaMeLink = (phoneE164, text) => {
    if (!phoneE164) return null;
    const phoneDigits = phoneE164.replace(/\D/g, "");
    return `https://wa.me/${phoneDigits}?text=${encodeURIComponent(text || "")}`;
  };

  // Envío directo: template aprobado (sendCollectionMessage) o, si no hay,
  // texto libre por sendCollectionWhatsApp (Meta Cloud, funciona con la ventana
  // de 24h abierta). En los dos el destinatario sale del userid y lo resuelve el
  // servidor. Si falla, ofrece abrir wa.me como fallback en una nueva pestaña.
  const handleSendWhatsAppDirect = async () => {
    if (!planPreview) return;
    const userid = planPreview.userid;
    const stage = planPreview.stage;
    const text = (editedWhatsapp || "").trim();
    if (!text) {
      showToast("El mensaje está vacío.", "error");
      return;
    }
    setSendingDirect("whatsapp");
    try {
      // Sprint 3 — Si hay template APROBADO y HABILITADO para este edificio,
      // usar la callable `sendCollectionMessage` que invoca notifyUser() con
      // template aprobado (no requiere ventana 24h, respeta preferencias,
      // quiet hours, rate limit, fallback email automático).
      const availability = templateAvailability[stage];
      if (availability?.approved && availability?.enabledForBuilding) {
        try {
          const result = await sendCollectionMessageCallable({
            buildingId,
            userid,
            stage,
            messageOverride: text,
          });
          if (result?.success) {
            showToast(
              result.channel === "email"
                ? `Enviado por email a ${planPreview.resident_name} (WhatsApp no estaba disponible — fallback automático).`
                : `Enviado por WhatsApp con template aprobado a ${planPreview.resident_name}.`,
              "success"
            );
            await logCollectionSent(
              result.channel === "email" ? "auto_email" : "auto_whatsapp",
              text
            );
            return;
          }
          if (result?.skipped) {
            showToast(`No enviado: ${result.reason || "preferencia o rate limit"}.`, "info");
            return;
          }
        } catch (callableErr) {
          // Si la callable indica que tenemos que caer al fallback, seguimos
          // con el flujo de raw send / wa.me. Si fue otro error, lo
          // mostramos y abortamos.
          if (callableErr.motivo === "solo_alquiler") {
            showToast("Es deuda de alquiler del edificio: las etapas de cobranza son de gastos comunes y no se le mandan.", "warning");
            return;
          }
          if (!callableErr.shouldFallback) {
            console.error("[A4] sendCollectionMessage callable error:", callableErr);
            showToast(`Error con template aprobado: ${callableErr.message}. Probá "Abrir wa.me".`, "error");
            return;
          }
          console.warn("[A4] template no aplicable, cayendo a fallback:", callableErr.fallbackReason);
        }
      }

      // Fallback: sin template aprobado/habilitado. Texto libre (funciona si
      // hay ventana 24h abierta) por userid (contrato K3): el servidor resuelve
      // el celular de la ficha del residente en este edificio; el cliente ya no
      // elige a qué número sale. Si el servidor o Meta lo rechazan → wa.me.
      // Edificio del envío, fijado ANTES de los await: el saliente se graba en
      // `messages` con este buildingId (contrato C3) y la vigilancia de entrega
      // lo busca con el mismo, aunque el admin cambie de edificio mientras tanto.
      const envioBuildingId = buildingId;
      try {
        const sendResult = await sendCollectionWhatsApp({ buildingId: envioBuildingId, userid, message: text });
        const metaMsgId = sendResult?.messageId;
        showToast(`Mensaje aceptado por Meta. Confirmando entrega…`, "info");
        await logCollectionSent("auto_whatsapp", text);
        // Watch del delivery status. Meta confirma async via webhook
        // (whatsappWebhook → updateStatusByMetaId). Si en 20s el doc en
        // `messages` queda en "failed", revertimos y caemos al fallback wa.me.
        if (metaMsgId) {
          watchWhatsAppDelivery({
            metaMessageId: metaMsgId,
            buildingId: envioBuildingId,
            text,
            residentName: planPreview.resident_name,
            userid,
          });
        } else {
          showToast(`Enviado por WhatsApp a ${planPreview.resident_name}.`, "success");
        }
      } catch (sendErr) {
        // El servidor no envió (sin celular en la ficha, ventana de 24 h
        // cerrada —error 131047 de Meta—, permiso): wa.me con el celular de la
        // ficha, que el staff abre a mano.
        console.warn("[A4] sendCollectionWhatsApp falló, ofreciendo wa.me:", sendErr?.code || "", sendErr?.message || sendErr);
        const phone = normalizePhoneUY(await resolveResidentPhone(userid, envioBuildingId));
        const link = buildWaMeLink(phone, text);
        if (link) {
          window.open(link, "_blank", "noopener,noreferrer");
          showToast(`No salió el envío automático (${sendErr.message || "ventana 24h cerrada o template no aprobado"}). Abrí WhatsApp Web con el mensaje precargado.`, "info");
          await logCollectionSent("manual_whatsapp", text);
        } else {
          showToast(`No se pudo enviar a ${planPreview.resident_name}: ${sendErr.message || "no hay teléfono cargado"}. Cargá el celular en su perfil o usá email.`, "error");
          setLastError({ context: "send-whatsapp", message: sendErr.message || "Residente sin teléfono cargado." });
        }
      }
    } finally {
      setSendingDirect(null);
    }
  };

  // Sprint 5 — Derivar caso a gestor legal. Persiste legal_handoff/{id} +
  // notifica al admin del edificio por email. Solo para casos Etapa 4-5.
  const [derivingLegal, setDerivingLegal] = useState(false);
  const [legalHandoff, setLegalHandoff] = useState(null);
  const handleDeriveLegal = async () => {
    if (!planPreview || !buildingId) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`¿Derivar el caso de ${planPreview.resident_name} a gestor legal? Se notifica al admin del edificio por email.`)) {
      return;
    }
    setDerivingLegal(true);
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(`${HABI_API_BASE_URL}/api/collection-plan/derive-legal`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          building_id: String(buildingId),
          userid: String(planPreview.userid),
          notes: "Derivado desde Cobranzas IA",
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        showToast(`No se pudo derivar: ${err.detail || resp.status}`, "error");
        return;
      }
      const data = await resp.json();
      setLegalHandoff(data);
      showToast(
        data.admin_notified
          ? "Caso derivado y admin notificado por email."
          : "Caso derivado (sin email — admin no tiene email cargado).",
        "success"
      );
    } catch (e) {
      showToast(`Error: ${e.message}`, "error");
    } finally {
      setDerivingLegal(false);
    }
  };

  // Sprint 3 fix — Watch del delivery status post-envío.
  // Meta API responde sincrónico OK con un messageId, pero la entrega real
  // se confirma via webhook (puede ser instantáneo o tardar segundos). Si
  // el residente no inició conversación en 24h o Meta lo bloquea, llega un
  // status "failed" que termina en `messages.status`. Esta función vigila
  // ese doc por hasta 20s y si llega failed:
  //   1. Marca el badge "✓ Enviado hoy" como problemático (lo quita).
  //   2. Toast de error con la causa real (reportada por Meta).
  //   3. Abre wa.me en nueva pestaña como fallback.
  // La consulta fija el edificio (metaMessageId + buildingId in): con las
  // reglas 0D el admin solo lee los mensajes de su edificio.
  const watchWhatsAppDelivery = ({ metaMessageId, buildingId: envioBuildingId, text, residentName, userid }) => {
    if (envioBuildingId == null || envioBuildingId === "") {
      showToast(`Enviado por WhatsApp a ${residentName}.`, "success");
      return;
    }
    const q = consultaEntregaWhatsApp(db, metaMessageId, envioBuildingId);
    let settled = false;
    const unsub = onSnapshot(q, (snap) => {
      if (snap.empty || settled) return;
      const data = snap.docs[0].data();
      const status = (data.status || "").toLowerCase();
      if (status === "failed") {
        settled = true;
        unsub();
        const reason = data.failureReason || "Meta rechazó la entrega (ventana 24h cerrada o template no aprobado).";
        showToast(`No se entregó a ${residentName}: ${reason}. Abriendo wa.me como respaldo…`, "error");
        // El celular, solo para el wa.me: la ficha del residente en el edificio del envío.
        resolveResidentPhone(userid, envioBuildingId).then((raw) => {
          const link = buildWaMeLink(normalizePhoneUY(raw), text);
          if (link) window.open(link, "_blank", "noopener,noreferrer");
        });
        // Revertir el badge "Enviado hoy" del local state (el log server
        // queda como auditoría pero el admin debe saber que el push falló).
        setSentByUserid((prev) => {
          const next = { ...prev };
          delete next[String(userid)];
          return next;
        });
      } else if (status === "delivered" || status === "read") {
        settled = true;
        unsub();
        showToast(`✓ Entregado por WhatsApp a ${residentName}.`, "success");
      }
    }, (err) => {
      console.warn("[A4] watchWhatsAppDelivery error:", err);
    });
    // Timeout — si pasaron 20s sin updates terminales, asumimos éxito tentativo.
    setTimeout(() => {
      if (settled) return;
      settled = true;
      unsub();
      showToast(`Enviado por WhatsApp a ${residentName}. (No recibimos confirmación de entrega en 20s — revisá Conversaciones WhatsApp si dudás.)`, "success");
    }, 20000);
  };

  // Abre wa.me directo sin intentar Meta (más rápido, 1 click)
  const handleOpenWaMe = async () => {
    if (!planPreview) return;
    const userid = planPreview.userid;
    const text = (editedWhatsapp || "").trim();
    if (!text) {
      showToast("El mensaje está vacío.", "error");
      return;
    }
    const rawPhone = await resolveResidentPhone(userid);
    const phone = normalizePhoneUY(rawPhone);
    if (!phone) {
      showToast(`No hay teléfono cargado para ${planPreview.resident_name}.`, "error");
      return;
    }
    const link = buildWaMeLink(phone, text);
    window.open(link, "_blank", "noopener,noreferrer");
    await logCollectionSent("manual_whatsapp", text);
  };

  // Sprint 2 — Persistir auditoria de envio manual.
  // Marca el mensaje como "enviado por X canal" en `collections_sent`
  // (idempotente por buildingId + userid + period_actual + stage). No envia
  // automaticamente — eso requiere templates Meta aprobados y la callable
  // sendCollectionMessage. Hasta entonces, el admin copia y manda manual y
  // hace click aca para que quede registrado.
  const logCollectionSent = async (channel, overrideText) => {
    if (!planPreview || !buildingId) return;
    const userid = planPreview.userid;
    const stage = planPreview.stage;
    const messageText = overrideText ?? (
      channel === "manual_email" || channel === "auto_email"
        ? planPreview.message?.email_html || ""
        : editedWhatsapp || planPreview.message?.whatsapp_text || ""
    );
    setSendingLog({ userid, channel });
    try {
      const headers = await getAuthHeaders();
      const url = `${HABI_API_BASE_URL}/api/collection-plan/log-sent`;
      const resp = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify({
          building_id: String(buildingId),
          userid: String(userid),
          stage,
          channel,
          message_text: messageText,
        }),
      });
      if (!resp.ok) {
        // 404/405: el endpoint todavía no está deployado en Cloud Run.
        // No es un error funcional del usuario — solo no tenemos auditoría
        // server-side todavía. Mantenemos el badge local "Enviado hoy".
        if (resp.status === 404 || resp.status === 405) {
          console.warn("[A4] /log-sent no disponible (deploy backend pendiente):", resp.status);
          setSentByUserid((prev) => ({
            ...prev,
            [String(userid)]: { stage, channel, at: new Date().toISOString(), local_only: true },
          }));
          return;
        }
        const err = await resp.json().catch(() => ({}));
        const detail = err.detail || err.error || err.message || `HTTP ${resp.status}`;
        showToast(`No se pudo registrar el envío: ${detail}`, "error");
        setLastError({ context: "log-sent", message: detail, status: resp.status, body: err });
        return;
      }
      const data = await resp.json();
      setSentByUserid((prev) => ({
        ...prev,
        [String(userid)]: { stage, channel, at: data.sent_at || new Date().toISOString() },
      }));
      // Toast informativo solo cuando se "marca como enviado" manualmente (no
      // cuando viene del flujo de envío directo, que ya muestra su propio toast).
      if (channel.startsWith("manual_") && !overrideText) {
        const channelLabel = channel === "manual_whatsapp" ? "WhatsApp" : "Email";
        showToast(
          data.deduped
            ? `Ya había un envío registrado para esta etapa este mes — no se duplicó.`
            : `Marcado como enviado por ${channelLabel}. Queda registrado en el historial.`,
          "success"
        );
      }
    } catch (e) {
      // Falla de red: tampoco bloqueamos. Logueamos para diagnóstico.
      console.warn("[A4] /log-sent network error:", e);
      setSentByUserid((prev) => ({
        ...prev,
        [String(userid)]: { stage, channel, at: new Date().toISOString(), local_only: true },
      }));
    } finally {
      setSendingLog(null);
    }
  };

  // Descripciones legibles de las etapas, espejando STAGE_BY_DAYS del backend
  // (Habi_agent/collection_engine.py). Si el backend cambia los rangos, hay
  // que actualizar aca tambien — Sprint 2 expone esto desde el endpoint.
  const STAGE_INFO = {
    1: { label: "Recordatorio amable", range: "1-7 días vencidos", tone: "Asume despiste, tono cercano." },
    2: { label: "Recordatorio firme", range: "8-30 días vencidos", tone: "Firme pero cordial, monto exacto." },
    3: { label: "Intimación previa", range: "31-60 días vencidos", tone: "Formal, menciona intimación próxima." },
    4: { label: "Antesala carta-documento", range: "61-90 días vencidos", tone: "Muy formal, último aviso antes de escalación legal." },
    5: { label: "Derivación legal", range: "Más de 90 días", tone: "No se envía mensaje automático — caso para gestor legal." },
  };
  const stageTooltip = (stage) => {
    const info = STAGE_INFO[stage];
    if (!info) return `Etapa ${stage}`;
    return `Etapa ${stage} — ${info.label}\n${info.range}\n${info.tone}`;
  };
  const scoreTooltip = (item) => {
    const parts = [
      `${item.months_overdue ?? 0} mes(es) vencidos`,
      `$ ${Number(item.total_amount || 0).toLocaleString("es-UY")} adeudado`,
    ];
    if (item.open_payment_count > 1) parts.push(`${item.open_payment_count} pagos abiertos`);
    if (item.do_not_escalate_flag) parts.push("incidencia abierta — no escalar");
    return `Score ${item.score} — combina antigüedad, monto e historial de mora.\n${parts.join(" · ")}`;
  };

  const openCollectionPlan = () => {
    setShowCollectionPlan(true);
    if (!collectionPlan) fetchCollectionPlan();
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text || "");
      showToast(`${label || "Texto"} copiado al portapapeles.`, "success");
    } catch (e) {
      showToast("No se pudo copiar.", "error");
    }
  };

  /**
   * C2 — Genera carta-documento de intimación de pago a partir del moroso
   * en preview. Útil cuando el plan IA derivó a humano (etapa 5) o el
   * staff decidió escalar manualmente desde etapa 4.
   */
  const handleGenerateLetter = async () => {
    if (!planPreview || !buildingId) return;
    try {
      setGeneratingLetter(true);
      setLegalLetter(null);
      const summary = planPreview.summary || {};
      const breakdown = (summary.breakdown || []).map(
        (b) => `${b.label}: $UYU ${b.amount}`
      ).join("\n");
      const headers = await getAuthHeaders();
      const resp = await fetch(`${HABI_API_BASE_URL}/api/generate-legal-letter`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          building_id: String(buildingId),
          doc_type: "intimacion_pago",
          case_data: {
            nombre_completo_residente: planPreview.resident_name || "Residente",
            unidad: planPreview.resident_apt || "—",
            monto_total: String(summary.total_amount || ""),
            periodos_adeudados: `${summary.months_overdue || 0} meses`,
            detalle_periodos: breakdown,
            plazo_dias: "10",
          },
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        showToast(err.detail || "No se pudo generar la carta-documento.", "error");
        return;
      }
      const data = await resp.json();
      setLegalLetter(data);
      showToast("Carta-documento generada. Revisá antes de enviar por correo certificado.", "success");
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setGeneratingLetter(false);
    }
  };

  // Manual payment modal
  const [manualModal, setManualModal] = useState(null); // payment
  const [manualForm, setManualForm] = useState(() => formPagoManual());
  const [savingManual, setSavingManual] = useState(false);

  // Devuelve una lista ordenada de { key, label, value } a partir del breakdown de un pago.
  // Incluye built-ins (GC, FR, Multas, Int) + cualquier fondo custom que tenga el edificio (fondo_calefaccion, etc.).
  // El label corto se resuelve con friendlyBreakdownName usando los customFunds del edificio.
  const getBreakdownRows = (breakdown) => {
    if (!breakdown) return [];
    const shortLabel = (key) => {
      if (key === "gastos_comunes") return "GC";
      if (key === "fondo_reserva") return "FR";
      if (key === "multas") return "Multas";
      if (key === "reajuste") return "Reajuste";
      if (key === "intereses") return "Recargo por mora";
      return friendlyBreakdownName(key, selectedBuilding);
    };
    const builtinOrder = ["gastos_comunes", "fondo_reserva"];
    const trailingOrder = ["reajuste", "intereses"];
    const allKeys = Object.keys(breakdown);
    const customKeys = allKeys
      .filter((k) => !builtinOrder.includes(k) && !trailingOrder.includes(k) && k.startsWith("fondo_"));
    const multaKeys = allKeys.filter((k) => k.startsWith("multa__"));
    const cargoKeys = allKeys.filter((k) => k.startsWith("cargo__"));
    const amenityKeys = allKeys.filter((k) => k.startsWith("amenity__"));
    const orderedKeys = [...builtinOrder, ...customKeys, ...multaKeys, ...cargoKeys, ...amenityKeys, ...trailingOrder];
    return orderedKeys
      .map((key) => ({ key, label: shortLabel(key), value: Number(breakdown[key]) || 0 }))
      .filter((row) => row.value > 0);
  };

  useEffect(() => {
    if (!buildingId) return;
    fetchData();
  }, [buildingId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch apartments
      const aptQ = query(collection(db, "apartments"), where("building", "in", tolerantId(buildingId)));
      const aptSnap = await getDocs(aptQ);
      const apts = aptSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setApartments(apts);

      // Fetch payments — cierre de mes (montlyaccountId) + migración (isMigration)
      const payQ = query(collection(db, "payments"), where("buildingid", "in", tolerantId(buildingId)));
      const paySnap = await getDocs(payQ);
      const pays = paySnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((p) => p.montlyaccountId || p.isMigration);
      setPayments(pays);

      // Fetch users
      const userIds = new Set();
      apts.forEach((a) => {
        if (a.owner) userIds.add(String(a.owner));
        if (a.tenant) userIds.add(String(a.tenant));
      });
      pays.forEach((p) => { if (p.userid) userIds.add(String(p.userid)); });

      // Nombres: fichas del edificio (reglas 0D, C10: accessBids array-contains).
      // Si no se pueden leer, la cobranza se muestra igual con "ID n" y se avisa.
      if (userIds.size > 0) {
        try {
          const users = await fetchUsersByUserIds([...userIds], buildingId);
          const map = {};
          users.forEach((u) => {
            map[String(u.userid)] = u.NombreCompleto || u.name || u.email || `ID ${u.userid}`;
          });
          setUsersMap(map);
        } catch (usersErr) {
          console.error("Cobranza: no se pudieron leer las fichas de los residentes:", usersErr?.code || usersErr?.message || usersErr);
          setUsersMap({});
          showToast("No se pudieron leer los nombres de los residentes: se muestran por número de usuario.", "warning");
        }
      } else {
        setUsersMap({});
      }
    } catch (err) {
      console.error("Error fetching cobranza data:", err);
      showToast("Error al cargar datos", "error");
    } finally {
      setLoading(false);
    }
  };

  // Compute per-unit data.
  //
  // Reglas de matching de payment → unidad:
  //   1. Si payment.apartmentId existe → matchea con esa unidad y solo esa.
  //   2. Si payment.unitId existe (manual) → matchea con esa unidad.
  //   3. Si NO tiene apartmentId/unitId (multas, amenity, intereses sin apt,
  //      legacy completo): matchea heurísticamente por userid contra
  //      owner/tenant de la unidad. Esto permite mostrar multas/amenity del
  //      propietario o inquilino que se generaron sin asociación a una
  //      unidad específica, en la unidad donde el usuario es residente.
  //   4. Caso edge: si el usuario es residente de N unidades del MISMO
  //      edificio y un payment legacy/multa no tiene apartmentId, va a
  //      aparecer en TODAS sus unidades (duplicación visual). Es trade-off
  //      conocido del legacy; el cierre nuevo asigna apartmentId siempre
  //      que el usuario tenga al menos una unidad en el cierre.
  const unitData = useMemo(() => {
    const units = apartments.map((apt) => {
      const unitPayments = payments.filter((p) => {
        if (p.apartmentId) return p.apartmentId === apt.id;
        if (p.unitId) return p.unitId === apt.id;
        // Sin apartmentId → fallback heurístico por userid.
        return (
          String(p.userid) === String(apt.owner) ||
          String(p.userid) === String(apt.tenant) ||
          p.userid === `unit_${apt.id}`
        );
      });

      const ownerPayments = unitPayments.filter((p) =>
        String(p.userid) === String(apt.owner) || p.userid === `unit_${apt.id}`
      );
      const tenantPayments = unitPayments.filter((p) =>
        apt.tenant && String(p.userid) === String(apt.tenant)
      );

      // Ocupantes SALIENTES: pagos asociados a la unidad (apartmentId) cuyo
      // userid ya no es el ocupante vigente (cambio de ocupante a mitad de mes).
      const isCurrentOccupant = (uid) =>
        String(uid) === String(apt.owner) ||
        String(uid) === String(apt.ownerSecondary) ||
        String(uid) === String(apt.tenant) ||
        String(uid) === String(apt.tenantSecondary) ||
        uid === `unit_${apt.id}`;
      const formerPayments = unitPayments.filter((p) => !isCurrentOccupant(p.userid));
      const formerGroups = {};
      formerPayments.forEach((p) => {
        const uid = String(p.userid);
        if (!formerGroups[uid]) {
          const pd = Array.isArray(p.prorationDetail) && p.prorationDetail[0] ? p.prorationDetail[0] : null;
          formerGroups[uid] = {
            userid: uid,
            name: usersMap[uid] || `ID ${uid}`,
            role: pd?.role === "tenant" ? "Inquilino" : "Propietario",
            payments: [],
          };
        }
        formerGroups[uid].payments.push(p);
      });
      const formerOccupants = Object.values(formerGroups).map((g) => ({
        ...g,
        payments: g.payments.sort((a, b) => (b.year * 12 + b.month) - (a.year * 12 + a.month)),
      }));

      // Deuda en pesos enteros: un resto de centésimos (p. ej. 0,22) no es deuda ni
      // pone la unidad en mora.
      const deudaDePago = (p) => roundPesos(Number(p.remainingAmount) || Number(p.amount) || 0);
      const esDeuda = (p) => !p.paid && p.status !== "rechazado" && deudaDePago(p) > 0;
      const sumDebt = (arr) => arr.filter(esDeuda).reduce((s, p) => s + deudaDePago(p), 0);
      const ownerDebt = sumDebt(ownerPayments);
      const tenantDebt = sumDebt(tenantPayments);
      const formerDebt = sumDebt(formerPayments);

      // Crédito a favor por sobrepago: mismo criterio que el cierre
      // (creditApplication.availableCreditByHolder) → sumar los remainingAmount
      // NEGATIVOS de los pagos VERIFICADOS del ocupante. Lo que el residente informó y
      // el staff todavía no revisó (pendiente_verificacion) o rechazó no es saldo a
      // favor. Se expone APARTE de la deuda para no romper morosidad/KPIs (que usan
      // totalDebt). Antes Cobranza descartaba el crédito (el filtro !p.paid lo excluía).
      // En pesos enteros (como el cierre): un sobrepago de 0,48 no es saldo a favor.
      const sumCredit = (arr) => {
        const disponible = roundPesos(arr.reduce((s, p) => {
          const bruto = sobrepagoAcreditable(p);          // sobrepago verificado (crédito bruto)
          const applied = Number(p.paidFromCredit) || 0; // crédito ya consumido para cubrir meses
          return s + bruto - applied;                     // disponible = bruto − aplicado
        }, 0));
        return disponible > 0 ? -disponible : 0;          // ≤ 0, como antes
      };
      const ownerCredit = sumCredit(ownerPayments);
      const tenantCredit = sumCredit(tenantPayments);
      const formerCredit = sumCredit(formerPayments);
      // Para transparencia en el header: sobrepago BRUTO y lo ya APLICADO a meses,
      // de modo que "disponible = bruto − aplicado" quede a la vista y no confunda.
      const allUnitPays = [...ownerPayments, ...tenantPayments, ...formerPayments];
      const grossCredit = roundPesos(allUnitPays.reduce((s, p) => s + sobrepagoAcreditable(p), 0));
      const appliedCredit = roundPesos(allUnitPays.reduce((s, p) => s + (Number(p.paidFromCredit) || 0), 0));

      // Deuda impaga (bruta) y saldo a favor DISPONIBLE (bruto − ya aplicado). NO se
      // netean en el display: el crédito se aplica DE VERDAD en el cierre
      // (computeCreditApplications) marcando el mes cubierto como pagado (paidFromCredit),
      // y ahí baja el saldo disponible y desaparece la deuda del mes. Así el flujo real
      // es "deuda $X + saldo a favor $Y" → tras el cierre "saldo a favor $(Y−X)".
      const grossDebt = ownerDebt + tenantDebt + formerDebt;
      const availableCredit = ownerCredit + tenantCredit + formerCredit; // ≤ 0

      const pendingVerification = unitPayments.filter((p) => p.status === "pendiente_verificacion").length;

      // Max overdue days
      const now = new Date();
      const parseMaturity = (m) => {
        if (!m) return null;
        if (typeof m === "string") {
          // Strings YYYY-MM-DD se interpretan como UTC; forzar timezone local
          return /^\d{4}-\d{2}-\d{2}$/.test(m) ? new Date(m + "T00:00:00") : new Date(m);
        }
        if (m.toDate) return m.toDate();
        return new Date(m);
      };
      const hasOverduePayment = (payArr, daysThreshold = 0) => {
        return payArr.some((p) => {
          if (!esDeuda(p)) return false;
          const matDate = parseMaturity(p.maturity);
          if (!matDate || isNaN(matDate.getTime())) return false;
          return Math.floor((now - matDate) / (1000 * 60 * 60 * 24)) > daysThreshold;
        });
      };

      let maxOverdue = 0;
      unitPayments.filter((p) => !p.paid && deudaDePago(p) > 0).forEach((p) => {
        if (p.maturity) {
          const matDate = parseMaturity(p.maturity);
          if (matDate && !isNaN(matDate.getTime())) {
            const diff = Math.floor((now - matDate) / (1000 * 60 * 60 * 24));
            if (diff > maxOverdue) maxOverdue = diff;
          }
        }
      });

      const ownerOverdue = hasOverduePayment(ownerPayments, 0);
      const tenantOverdue = hasOverduePayment(tenantPayments, 0);
      const ownerOverdue30 = hasOverduePayment(ownerPayments, 30);
      const tenantOverdue30 = hasOverduePayment(tenantPayments, 30);
      const ownerOverdue90 = hasOverduePayment(ownerPayments, 90);
      const tenantOverdue90 = hasOverduePayment(tenantPayments, 90);

      return {
        apt,
        unitNumber: apt.number || apt.id,
        ownerName: esAlquilerDelEdificio(apt)
          ? "Alquiler del edificio"
          : usersMap[String(apt.owner)] || (apt.owner ? `ID ${apt.owner}` : "Sin propietario"),
        // Unidad del edificio (portería, alquiler): su fila se ve, pero no cuenta en la morosidad.
        delEdificio: esUnidadDelEdificio(apt),
        tenantName: apt.tenant ? (usersMap[String(apt.tenant)] || `ID ${apt.tenant}`) : null,
        ownerDebt,
        tenantDebt,
        formerDebt,
        formerOccupants,
        totalDebt: grossDebt,          // deuda impaga (bruta)
        totalCredit: availableCredit,  // saldo a favor disponible (≤ 0)
        grossCredit,                   // sobrepago bruto (≥ 0)
        appliedCredit,                 // crédito ya aplicado a meses (≥ 0)
        pendingVerification,
        maxOverdue,
        ownerOverdue,
        tenantOverdue,
        ownerOverdue30,
        tenantOverdue30,
        ownerOverdue90,
        tenantOverdue90,
        ownerPayments: ownerPayments.sort((a, b) => (b.year * 12 + b.month) - (a.year * 12 + a.month)),
        tenantPayments: tenantPayments.sort((a, b) => (b.year * 12 + b.month) - (a.year * 12 + a.month)),
        allPayments: unitPayments.sort((a, b) => (b.year * 12 + b.month) - (a.year * 12 + a.month)),
      };
    });

    return units.sort((a, b) => (a.unitNumber || "").localeCompare(b.unitNumber || "", undefined, { numeric: true }));
  }, [apartments, payments, usersMap]);

  // Stats (de gastos comunes: sin las unidades del edificio, cuyo inquilino debe alquiler)
  const stats = useMemo(() => {
    const soloGc = unitData.filter((u) => !u.delEdificio);
    const totalDebt = soloGc.reduce((s, u) => s + u.totalDebt, 0);
    const unitsInDebt = soloGc.filter((u) => u.totalDebt > 0).length;
    const pendVerif = payments.filter((p) => p.status === "pendiente_verificacion").length;

    // Morosidad: solo cuenta unidades con pagos vencidos (maturity < hoy)
    const unitsOverdue = soloGc.filter((u) => u.ownerOverdue || u.tenantOverdue).length;
    const morosidad = soloGc.length > 0 ? Math.round((unitsOverdue / soloGc.length) * 100) : 0;

    // Morosidad +30d y +90d desglosado por rol
    const morosidad30 = {
      propietarios: soloGc.filter((u) => u.ownerOverdue30).length,
      inquilinos: soloGc.filter((u) => u.tenantOverdue30).length,
    };
    const morosidad90 = {
      propietarios: soloGc.filter((u) => u.ownerOverdue90).length,
      inquilinos: soloGc.filter((u) => u.tenantOverdue90).length,
    };

    return { totalDebt, unitsInDebt, totalUnits: soloGc.length, pendVerif, morosidad, morosidad30, morosidad90 };
  }, [unitData, payments]);

  // Residentes cuya deuda abierta es solo alquiler: las etapas de cobranza IA son
  // de gastos comunes, así que a ellos no se les genera mensaje.
  const deudaSoloAlquiler = useMemo(() => {
    const abiertos = {};
    payments.forEach((p) => {
      if (p.paid || p.status === "rechazado" || saldoEnPesos(p) <= 0) return;
      const uid = String(p.userid);
      (abiertos[uid] ||= []).push(p);
    });
    return new Set(Object.entries(abiertos).filter(([, ps]) => ps.every(esPagoDeAlquiler)).map(([uid]) => uid));
  }, [payments]);

  // Filter
  const filteredUnits = unitData.filter((u) => {
    if (filter === "deuda" && u.totalDebt <= 0) return false;
    if (filter === "verificacion" && u.pendingVerification <= 0) return false;
    if (filter === "aldia" && u.totalDebt > 0) return false;
    if (search) {
      const s = search.toLowerCase();
      if (
        !(u.unitNumber || "").toLowerCase().includes(s) &&
        !u.ownerName.toLowerCase().includes(s) &&
        !(u.tenantName || "").toLowerCase().includes(s)
      ) return false;
    }
    return true;
  });

  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const getMonthName = (m) => {
    const months = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
    return months[(m || 1) - 1] || "";
  };

  // Marcar multas como pagadas cuando el pago está completo
  const markMultasPaid = async (payment) => {
    const multaIds = payment.multaIds || [];
    if (multaIds.length === 0) return;
    for (const multaId of multaIds) {
      try {
        await updateDoc(doc(db, "multas", multaId), {
          status: "pagada",
          paidAt: Timestamp.now(),
        });
      } catch (err) {
        console.error("Error marcando multa como pagada:", multaId, err);
      }
    }
  };

  // Aprobar pago — con transaccion para evitar race conditions (FIN-004/FIN-007)
  const handleApprove = async (payment) => {
    if (avisoVerificacion === null) return showToast("Elegí si le avisamos al residente", "error");
    if (await isDemoUser()) return showToast(DEMO_BLOCK_MESSAGE, "warning");
    try {
      const payRef = doc(db, "payments", payment.id);
      let isPaid = false;
      // R4: el admin puede registrar el monto REALMENTE recibido al aprobar.
      // Si dejó el valor por defecto (lo declarado), no se pisa el paidAmount.
      const overrideRaw = verifyPaidInput.trim();
      const overridePaid = overrideRaw === "" ? null : Number(overrideRaw);
      const hasOverride = overridePaid != null && !isNaN(overridePaid) && overridePaid >= 0;
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(payRef);
        if (!snap.exists()) throw new Error("El pago no existe.");
        const data = snap.data();
        const total = Number(data.amount) || 0;
        const effectivePaid = hasOverride ? overridePaid : (Number(data.paidAmount) || 0);
        // remaining puede quedar negativo (crédito a favor) → se arrastra al mes siguiente.
        // Un resto de centésimos (deuda o saldo a favor) no queda: el pago se da por saldado.
        const resto = Math.round((total - effectivePaid) * 100) / 100;
        const remaining = roundPesos(resto) === 0 ? 0 : resto;
        isPaid = remaining <= 0;
        tx.update(payRef, {
          // Fully paid / crédito → aprobado; pagó de menos → parcial
          status: remaining <= 0 ? "aprobado" : "parcial",
          paid: isPaid,
          paidAmount: Math.round(effectivePaid * 100) / 100,
          remainingAmount: remaining,
          confirmedByAdmin: true,
          verifiedBy: auth.currentUser?.email || "",
          verifiedAt: Timestamp.now(),
          ...camposAviso(avisoVerificacion),
        });
      });
      // Si el pago quedó completo, marcar multas incluidas como pagadas
      if (isPaid) {
        await markMultasPaid(payment);
      }
      await logAudit("approve_payment", { buildingId: buildingId, entityId: payment.id, entityType: "payment", newValue: "aprobado", avisoResidente: avisoVerificacion });
      showToast(avisoVerificacion ? "Pago aprobado. Le avisamos al residente." : "Pago aprobado, sin aviso al residente.", "success");
      setVerifyModal(null);
      fetchData();
    } catch (err) {
      console.error("Error approving:", err);
      showToast("Error al aprobar", "error");
    }
  };

  // Rechazar pago — revierte paidAmount/remainingAmount (FIN-004)
  const handleReject = async (payment) => {
    if (await isDemoUser()) return showToast(DEMO_BLOCK_MESSAGE, "warning");
    if (!rejectReason.trim()) {
      showToast("Ingresá un motivo de rechazo", "error");
      return;
    }
    if (avisoVerificacion === null) {
      showToast("Elegí si le avisamos al residente", "error");
      return;
    }
    try {
      const payRef = doc(db, "payments", payment.id);
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(payRef);
        if (!snap.exists()) throw new Error("El pago no existe.");
        const data = snap.data();

        // Revertir el ultimo intento de pago
        const lastAttempt = Number(data.lastPaymentAttempt) || 0;
        const currentPaid = Number(data.paidAmount) || 0;
        const total = Number(data.amount) || 0;
        const revertedPaid = Math.max(0, currentPaid - lastAttempt);
        const revertedRemaining = Math.min(total, Math.round((total - revertedPaid) * 100) / 100);

        tx.update(payRef, {
          status: "rechazado",
          paidAmount: Math.round(revertedPaid * 100) / 100,
          remainingAmount: revertedRemaining,
          paid: false,
          rejectionReason: rejectReason.trim(),
          verifiedBy: auth.currentUser?.email || "",
          verifiedAt: Timestamp.now(),
          lastPaymentAttempt: null,
          ...camposAviso(avisoVerificacion),
        });
      });
      await logAudit("reject_payment", { buildingId: buildingId, entityId: payment.id, entityType: "payment", newValue: "rechazado", reason: rejectReason.trim(), avisoResidente: avisoVerificacion });
      showToast(avisoVerificacion ? "Pago rechazado y monto revertido. Le avisamos al residente." : "Pago rechazado y monto revertido, sin aviso al residente.", "success");
      setVerifyModal(null);
      setRejectReason("");
      fetchData();
    } catch (err) {
      console.error("Error rejecting:", err);
      showToast("Error al rechazar", "error");
    }
  };

  // Registrar pago manual — con transaccion para evitar race conditions (FIN-007)
  const handleManualPayment = async () => {
    if (!manualModal || savingManual) return;
    const paidAmount = Number(manualForm.paidAmount);
    if (!paidAmount || paidAmount <= 0) {
      showToast("Ingresá un monto válido", "error");
      return;
    }
    if (!manualForm.paymentDate) {
      showToast("Ingresá la fecha del pago", "error");
      return;
    }
    const [py, pm, pd] = manualForm.paymentDate.split("-").map(Number);
    const paymentDate = new Date(py, pm - 1, pd, 12, 0, 0); // mediodia local para evitar corrimiento de dia
    if (isNaN(paymentDate.getTime())) {
      showToast("Fecha de pago inválida", "error");
      return;
    }
    if (manualForm.aviso === null) {
      showToast("Elegí si le avisamos al residente", "error");
      return;
    }
    // Bloquear ANTES de cualquier await para evitar doble click / doble registro
    setSavingManual(true);
    try {
      if (await isDemoUser()) {
        showToast(DEMO_BLOCK_MESSAGE, "warning");
        return;
      }
      // El archivo se sube antes de la transacción: si falla, no se registra el pago.
      let proofUrl = null;
      if (manualForm.proof) {
        const fileRef = ref(storage, `payment_proofs/${manualModal.id}/${Date.now()}_${manualForm.proof.name}`);
        await uploadBytes(fileRef, manualForm.proof);
        proofUrl = await getDownloadURL(fileRef);
      }
      let newRemaining = 0;
      const payRef = doc(db, "payments", manualModal.id);
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(payRef);
        if (!snap.exists()) throw new Error("El pago no existe.");
        const data = snap.data();
        // Guard: un período ya saldado (p. ej. cubierto entero con saldo a favor) NO se
        // paga a mano: duplicaba paidAmount y creaba un crédito espurio. Si el saldo a
        // favor cubrió solo una parte, se registra lo que falta (antes cualquier
        // paidFromCredit > 0 lo bloqueaba, aunque fueran 0,48 de redondeo).
        if (data.paid === true) {
          throw new Error("Este período ya está pagado; no se puede registrar un pago manual encima.");
        }
        const currentPaid = Number(data.paidAmount) || 0;
        const total = Number(data.amount) || 0;
        const newPaidTotal = currentPaid + paidAmount;
        // R4: permitir sobrepago (crédito a favor). remainingAmount puede ser negativo
        // (se arrastra como crédito al mes siguiente — ver fundBalances.buildUnitRow).
        // Un resto de centésimos (deuda o saldo a favor) no queda: el pago se da por saldado.
        const resto = Math.round((total - newPaidTotal) * 100) / 100;
        newRemaining = roundPesos(resto) === 0 ? 0 : resto;

        tx.update(payRef, {
          paidAmount: Math.round(newPaidTotal * 100) / 100,
          remainingAmount: newRemaining,
          paid: newRemaining <= 0,
          // R4: "parcial" (pagó de menos) es distinto de "pendiente" (nunca pagó).
          status: newRemaining <= 0 ? "aprobado" : "parcial",
          lastPaymentAttempt: Math.round(paidAmount * 100) / 100,
          paymentMethod: manualForm.paymentMethod,
          adminNotes: manualForm.adminNotes || null,
          adminPaymentDate: Timestamp.fromDate(paymentDate),
          confirmedByAdmin: true,
          verifiedBy: auth.currentUser?.email || "",
          verifiedAt: Timestamp.now(),
          ...camposAviso(manualForm.aviso),
          // En un pago parcial que se completa después, el comprobante anterior no se pierde.
          ...(proofUrl
            ? {
                paymentProof: proofUrl,
                ...(data.paymentProof ? { comprobantesAnteriores: [...(data.comprobantesAnteriores || []), data.paymentProof] } : {}),
              }
            : {}),
        });
      });
      // Si el pago quedó completo (o con crédito), marcar multas incluidas como pagadas
      if (newRemaining <= 0) {
        await markMultasPaid(manualModal);
      }
      await logAudit("manual_payment", { buildingId: buildingId, entityId: manualModal.id, entityType: "payment", newValue: { paidAmount, method: manualForm.paymentMethod, paymentDate: manualForm.paymentDate, paymentProof: proofUrl, avisoResidente: manualForm.aviso } });
      const msg = newRemaining < 0
        ? `Pago con crédito a favor de ${formatCurrencyUY(Math.abs(newRemaining))} (se arrastra al mes siguiente)`
        : newRemaining === 0
        ? "Pago completo registrado"
        : `Pago parcial registrado. Resta: ${formatCurrencyUY(newRemaining)}`;
      showToast(`${msg}${manualForm.aviso ? ". Le avisamos al residente." : ", sin aviso al residente."}`, "success");
      setManualModal(null);
      setManualForm(formPagoManual());
      fetchData();
    } catch (err) {
      console.error("Error registering payment:", err);
      showToast(err?.message || "Error al registrar pago", "error");
    } finally {
      setSavingManual(false);
    }
  };

  if (!buildingId) {
    return (
      <div style={styles.container}>
        <EmptyState icon={FaBuilding} title="Seleccioná un edificio para ver la cobranza" />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}><FaMoneyBillWave style={{ marginRight: 8 }} /> Cobranza</h2>
          <p style={styles.subtitle}>Gestión de cobros por unidad</p>
        </div>
        <button
          type="button"
          onClick={openCollectionPlan}
          style={{
            padding: "10px 18px",
            fontSize: typography.sizes.sm,
            fontWeight: 400,
            color: colors.white,
            background: colors.primary,
            border: "none",
            borderRadius: 8,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
          }}
          aria-label="Abrir cobranzas IA"
        >
          <FaMoneyBillWave size={14} />
          <span>Cobranzas IA</span>
        </button>
      </div>

      {/* Legal banner */}
      <div style={styles.legalBanner}>
        <FaInfoCircle size={14} />
        La responsabilidad del pago recae sobre el propietario (Ley 14.219, Art. 77). Inquilinos no pagan gastos de mejora.
      </div>

      {/* KPIs — componente KPICard del design system */}
      <div style={styles.kpiGrid} data-tutorial="cobranza-kpis">
        <KPICard
          icon={FaBuilding}
          iconBg={colors.primaryLighter}
          iconColor={colors.primary}
          label="Unidades"
          value={stats.totalUnits}
        />

        {/* KPI 1a: Morosidad por Unidades */}
        <div style={{ position: "relative" }}>
          <KPICard
            icon={FaPercentage}
            iconBg={colors.warningLight}
            iconColor={colors.warning}
            label="Morosidad (Unidades)"
            value={`${stats.morosidad}%`}
            badge={`${stats.unitsInDebt} de ${stats.totalUnits}`}
            badgeColor={getKpiBadgeColor("tasa_morosidad_unidades", stats.morosidad)}
          />
          <div style={styles.tooltipWrap}>
            <KpiInfoTooltip kpiKey="tasa_morosidad_unidades" />
          </div>
        </div>

        {/* KPI 1b: Morosidad por Monto */}
        <div style={{ position: "relative" }}>
          <KPICard
            icon={FaMoneyBillWave}
            iconBg={colors.errorLight}
            iconColor={colors.error}
            label="Deuda Vencida"
            value={formatCurrencyUY(stats.totalDebt)}
            badgeColor={getKpiBadgeColor("tasa_morosidad_monto", stats.totalDebt > 0 ? (stats.totalDebt / Math.max(1, stats.totalDebt + 1)) * 100 : 0)}
          />
          <div style={styles.tooltipWrap}>
            <KpiInfoTooltip kpiKey="tasa_morosidad_monto" />
          </div>
        </div>

        <KPICard
          icon={FaClock}
          iconBg={colors.infoLight}
          iconColor={colors.info}
          label="Pend. Verificación"
          value={stats.pendVerif}
          badge={stats.pendVerif > 0 ? "Requiere acción" : undefined}
          badgeColor="warning"
        />
      </div>

      {/* Mini-aging: desglose por antiguedad */}
      <div style={styles.agingRow} data-tutorial="cobranza-aging">
        <div style={styles.agingHeader}>
          <span style={styles.agingSectionTitle}>Antigüedad de mora</span>
          <KpiInfoTooltip kpiKey="aging_deuda" />
        </div>
        <div style={styles.agingBuckets}>
          <div style={{ ...styles.agingBucket, borderTopColor: colors.success }}>
            <span style={styles.agingBucketLabel}>0-30d</span>
            <span style={styles.agingBucketValue}>
              {unitData.filter(u => u.maxOverdue > 0 && u.maxOverdue <= 30).length} uds
            </span>
          </div>
          <div style={{ ...styles.agingBucket, borderTopColor: colors.info }}>
            <span style={styles.agingBucketLabel}>31-60d</span>
            <span style={styles.agingBucketValue}>
              {unitData.filter(u => u.maxOverdue > 30 && u.maxOverdue <= 60).length} uds
            </span>
          </div>
          <div style={{ ...styles.agingBucket, borderTopColor: colors.warning }}>
            <span style={styles.agingBucketLabel}>61-90d</span>
            <span style={styles.agingBucketValue}>
              {unitData.filter(u => u.maxOverdue > 60 && u.maxOverdue <= 90).length} uds
            </span>
          </div>
          <div style={{ ...styles.agingBucket, borderTopColor: colors.error }}>
            <span style={styles.agingBucketLabel}>91-180d</span>
            <span style={styles.agingBucketValue}>
              {unitData.filter(u => u.maxOverdue > 90 && u.maxOverdue <= 180).length} uds
            </span>
          </div>
          <div style={{ ...styles.agingBucket, borderTopColor: colors.errorDark }}>
            <span style={styles.agingBucketLabel}>180+d</span>
            <span style={styles.agingBucketValue}>
              {unitData.filter(u => u.maxOverdue > 180).length} uds
            </span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filterRow}>
        <div style={styles.searchBox}>
          <FaSearch size={14} color={colors.gray400} />
          <input type="text" placeholder="Buscar unidad o usuario..." value={search} onChange={(e) => setSearch(e.target.value)} style={styles.searchInput} />
        </div>
        <div style={styles.filterTabs}>
          {[
            { key: "todas", label: "Todas" },
            { key: "deuda", label: "En deuda" },
            { key: "verificacion", label: "Pend. verif." },
            { key: "aldia", label: "Al día" },
          ].map((f) => (
            <button key={f.key} style={{ ...styles.filterTab, ...(filter === f.key ? styles.filterTabActive : {}) }} onClick={() => setFilter(f.key)}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Units list */}
      {loading ? (
        <LoadingState variant="block" message="Cargando…" />
      ) : filteredUnits.length === 0 ? (
        <EmptyState icon={FaMoneyBillWave} title="No hay unidades que coincidan" />
      ) : (
        <div style={styles.list} data-tutorial="cobranza-lista">
          {filteredUnits.map((unit) => (
            <div key={unit.apt.id} style={styles.unitCard}>
              {/* Unit header row */}
              <div style={styles.unitHeader} onClick={() => toggleExpand(unit.apt.id)}>
                <div style={styles.unitLeft}>
                  {expanded[unit.apt.id] ? <FaChevronDown size={12} color={colors.gray600} /> : <FaChevronRight size={12} color={colors.gray600} />}
                  <span style={styles.unitNumber}>Unidad {unit.unitNumber}</span>
                  {unit.pendingVerification > 0 && (
                    <span style={styles.verifBadge}>{unit.pendingVerification} pend.</span>
                  )}
                </div>
                <div style={styles.unitRight}>
                  <div style={styles.unitUserCol}>
                    <span style={styles.unitUserLabel}>P: {unit.ownerName}</span>
                    {unit.tenantName && <span style={styles.unitUserLabel}>I: {unit.tenantName}</span>}
                  </div>
                  <div style={styles.unitDebtCol}>
                    {unit.totalDebt > 0 ? (
                      <>
                        <span style={{ ...styles.debtAmount, color: colors.error }}>{formatCurrencyUY(unit.totalDebt)}</span>
                        {unit.maxOverdue > 0 && <span style={styles.overdueBadge}>{unit.maxOverdue}d atraso</span>}
                        {unit.totalCredit < 0 && (
                          <span style={{ fontSize: typography.sizes.xs, color: colors.success }}>
                            Saldo a favor: {formatCurrencyUY(Math.abs(unit.totalCredit))}
                            {unit.appliedCredit > 0 && (
                              <span style={{ color: colors.gray500 }}> (sobrepago {formatCurrencyUY(unit.grossCredit)} − {formatCurrencyUY(unit.appliedCredit)} aplicado)</span>
                            )}
                          </span>
                        )}
                      </>
                    ) : unit.totalCredit < 0 ? (
                      <>
                        <span style={{ ...styles.debtAmount, color: colors.success }}>
                          Saldo a favor: {formatCurrencyUY(Math.abs(unit.totalCredit))}
                        </span>
                        {unit.appliedCredit > 0 && (
                          <span style={{ fontSize: typography.sizes.xs, color: colors.gray500 }}>
                            sobrepago {formatCurrencyUY(unit.grossCredit)} − {formatCurrencyUY(unit.appliedCredit)} aplicado
                          </span>
                        )}
                      </>
                    ) : (
                      <span style={{ ...styles.debtAmount, color: colors.success }}>Al día</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded: payment history */}
              {expanded[unit.apt.id] && (
                <div style={styles.unitBody}>
                  {/* Owner payments */}
                  {unit.ownerPayments.length > 0 && (
                    <div style={{ ...styles.roleSection, borderLeftColor: colors.primary }}>
                      <div style={styles.roleHeader}>
                        <span style={{ ...styles.rolePill, background: colors.primaryLighter, color: colors.primary }}>Propietario</span>
                        <span style={styles.roleName}>{unit.ownerName}</span>
                      </div>
                      <CollapsiblePayments payments={unit.ownerPayments} render={renderPayments} />
                    </div>
                  )}
                  {/* Tenant payments */}
                  {unit.tenantName && unit.tenantPayments.length > 0 && (
                    <div style={{ ...styles.roleSection, borderLeftColor: colors.info }}>
                      <div style={styles.roleHeader}>
                        <span style={{ ...styles.rolePill, background: colors.infoLight, color: colors.infoDark }}>Inquilino</span>
                        <span style={styles.roleName}>{unit.tenantName}</span>
                      </div>
                      <CollapsiblePayments payments={unit.tenantPayments} render={renderPayments} />
                    </div>
                  )}
                  {/* Ocupantes salientes (cambio de ocupante a mitad de mes) */}
                  {(unit.formerOccupants || []).map((fo) => (
                    <div key={fo.userid} style={{ ...styles.roleSection, borderLeftColor: colors.gray300 }}>
                      <div style={styles.roleHeader}>
                        <span style={{ ...styles.rolePill, background: colors.gray100, color: colors.gray600 }}>{fo.role} saliente</span>
                        <span style={{ ...styles.roleName, color: colors.gray600 }}>{fo.name}</span>
                      </div>
                      <CollapsiblePayments payments={fo.payments} render={renderPayments} />
                    </div>
                  ))}
                  {unit.allPayments.length === 0 && (
                    <p style={{ color: colors.gray400, fontSize: typography.sizes.base, padding: "8px 0" }}>Sin pagos registrados</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Verify modal */}
      <AdminModal
        open={!!verifyModal}
        onClose={() => { setVerifyModal(null); setRejectReason(""); }}
        title="Verificar pago"
        size="medium"
        footer={verifyModal && (
          <>
            <button style={{ ...styles.btnDanger, opacity: !rejectReason.trim() || avisoVerificacion === null ? 0.6 : 1 }} onClick={() => handleReject(verifyModal)} disabled={!rejectReason.trim() || avisoVerificacion === null}>
              <FaTimes size={12} /> Rechazar
            </button>
            <button style={{ ...styles.btnSuccess, opacity: avisoVerificacion === null ? 0.6 : 1 }} onClick={() => handleApprove(verifyModal)} disabled={avisoVerificacion === null}>
              <FaCheck size={12} /> Aprobar
            </button>
          </>
        )}
      >
        {verifyModal && (
          <>
            <p style={{ fontSize: typography.sizes.base, color: colors.gray900 }}>
              Monto: <strong>{formatCurrencyUY(verifyModal.amount)}</strong> — {getMonthName(verifyModal.month)} {verifyModal.year}
            </p>
            {verifyModal.paymentMethod && <p style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>Método: {verifyModal.paymentMethod}</p>}
            {verifyModal.lastPaymentAttempt != null && (
              <p style={{ fontSize: typography.sizes.sm, color: colors.gray600 }}>
                Informó: {formatCurrencyUY(verifyModal.lastPaymentAttempt)}
                {verifyModal.informedVia === "habi_chat" ? " (por Habi)" : ""}
              </p>
            )}
            {verifyModal.montoNoCierra && (
              <div style={{ marginTop: 8, padding: "8px 12px", borderRadius: radius.sm, background: colors.errorLight, color: colors.errorDark, fontSize: typography.sizes.sm }}>
                El comprobante es por {formatCurrencyUY(verifyModal.montoComprobante)} y lo que debía era {formatCurrencyUY(verifyModal.deudaAlInformar)}: no cierra. Revisalo antes de aprobar y cargá abajo el monto realmente recibido.
              </div>
            )}
            {(verifyModal.paymentProof || verifyModal.comprobanteUrl) && (
              <div style={{ marginTop: 8 }}>
                <button style={styles.btnSecondary} onClick={() => setShowProof(verifyModal.paymentProof || verifyModal.comprobanteUrl)}>
                  <FaFileImage size={12} /> Ver comprobante
                </button>
              </div>
            )}
            {verifyModal.breakdown && (
              <div style={{ marginTop: 12, fontSize: typography.sizes.sm, color: colors.gray600 }}>
                <strong>Desglose:</strong>
                {(() => {
                  const longLabel = (key) => {
                    if (key === "gastos_comunes") return "Gastos comunes";
                    if (key === "fondo_reserva") return "Fondo reserva";
                    if (key === "multas") return "Multas";
                    if (key === "reajuste") return "Reajuste (UI)";
                    if (key === "intereses") return "Recargo por mora";
                    return friendlyBreakdownName(key, selectedBuilding);
                  };
                  return getBreakdownRows(verifyModal.breakdown).map((row) => (
                    <div key={row.key}>{longLabel(row.key)}: {formatCurrencyUY(row.value)}</div>
                  ));
                })()}
              </div>
            )}
            {/* R4: monto realmente recibido (permite aprobar parcial o con crédito) */}
            <div style={{ marginTop: 16 }}>
              <label style={styles.label}>Monto realmente recibido</label>
              <input
                type="number"
                min="0"
                step="0.01"
                style={styles.input}
                value={verifyPaidInput}
                onChange={(e) => setVerifyPaidInput(e.target.value)}
              />
              {(() => {
                const total = Number(verifyModal.amount) || 0;
                const recv = Number(verifyPaidInput) || 0;
                const diff = roundPesos(total - recv);
                if (diff > 0) return <span style={{ fontSize: typography.sizes.sm, color: colors.warningDark }}>Pago parcial — resta {formatCurrencyUY(diff)} (se arrastra al mes siguiente).</span>;
                if (diff < 0) return <span style={{ fontSize: typography.sizes.sm, color: colors.info }}>Crédito a favor de {formatCurrencyUY(Math.abs(diff))} (se arrastra al mes siguiente).</span>;
                return <span style={{ fontSize: typography.sizes.sm, color: colors.successDark }}>Pago completo.</span>;
              })()}
            </div>
            <div style={{ marginTop: 16 }}>
              <label style={styles.label}>Motivo de rechazo (si aplica)</label>
              <textarea style={{ ...styles.input, minHeight: 50, resize: "vertical" }} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Solo requerido si rechazás..." />
            </div>
            <div style={{ marginTop: 16 }}>
              <PreguntaAviso nombre={usersMap[String(verifyModal.userid)]} valor={avisoVerificacion} onChange={setAvisoVerificacion} idBase="aviso-verificacion" />
            </div>
          </>
        )}
      </AdminModal>

      {/* Manual payment modal */}
      <AdminModal
        open={!!manualModal}
        onClose={() => { if (!savingManual) setManualModal(null); }}
        title="Registrar pago manual"
        size="medium"
        footer={manualModal && (
          <>
            <button style={styles.btnSecondary} onClick={() => setManualModal(null)} disabled={savingManual}>Cancelar</button>
            <button
              style={{ ...styles.btnPrimary, opacity: savingManual || manualForm.aviso === null ? 0.6 : 1, cursor: savingManual || manualForm.aviso === null ? "not-allowed" : "pointer" }}
              onClick={handleManualPayment}
              disabled={savingManual || manualForm.aviso === null}
            >
              {savingManual ? "Registrando…" : "Registrar pago"}
            </button>
          </>
        )}
      >
        {manualModal && (
          <>
            <p style={{ fontSize: typography.sizes.base, color: colors.gray900, margin: "0 0 16px" }}>
              Deuda: <strong>{formatCurrencyUY(roundPesos(Number(manualModal.remainingAmount) || Number(manualModal.amount)))}</strong> — {getMonthName(manualModal.month)} {manualModal.year}
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <label style={styles.label}>Monto pagado *</label>
                <input type="number" min="0" style={styles.input} value={manualForm.paidAmount} onChange={(e) => setManualForm({ ...manualForm, paidAmount: e.target.value })} placeholder="Monto" disabled={savingManual} />
              </div>
              <div>
                <label style={styles.label}>Fecha del pago *</label>
                <input type="date" style={styles.input} value={manualForm.paymentDate} max={getTodayInputDate()} onChange={(e) => setManualForm({ ...manualForm, paymentDate: e.target.value })} disabled={savingManual} aria-label="Fecha del pago" />
              </div>
              <div>
                <label style={styles.label}>Método de pago</label>
                <select style={styles.input} value={manualForm.paymentMethod} onChange={(e) => setManualForm({ ...manualForm, paymentMethod: e.target.value })} disabled={savingManual}>
                  <option value="transferencia">Transferencia</option>
                  <option value="efectivo">Efectivo</option>
                  <option value="cheque">Cheque</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
              <div>
                <span style={styles.label} id="manual-proof-titulo">
                  Comprobante de pago (opcional)
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="file"
                    id="manual-proof"
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    style={{ display: "none" }}
                    aria-labelledby="manual-proof-titulo"
                    disabled={savingManual}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = "";
                      if (!file) return;
                      if (!TIPOS_COMPROBANTE.includes(file.type)) {
                        showToast("Formato no permitido: solo PDF, JPG, PNG o WEBP", "warning");
                        return;
                      }
                      if (file.size > MAX_COMPROBANTE_MB * 1024 * 1024) {
                        showToast(`El archivo supera los ${MAX_COMPROBANTE_MB} MB`, "warning");
                        return;
                      }
                      setManualForm((f) => ({ ...f, proof: file }));
                    }}
                  />
                  <label htmlFor="manual-proof" style={styles.fileLabel}>
                    <FaUpload size={12} /> {manualForm.proof ? manualForm.proof.name : "Subir comprobante"}
                  </label>
                  {manualForm.proof && (
                    <button type="button" style={styles.btnSecondary} onClick={() => setManualForm((f) => ({ ...f, proof: null }))} disabled={savingManual} aria-label="Quitar comprobante">
                      <FaTimes size={12} />
                    </button>
                  )}
                </div>
              </div>
              <div>
                <label style={styles.label}>Notas (opcional)</label>
                <input type="text" style={styles.input} value={manualForm.adminNotes} onChange={(e) => setManualForm({ ...manualForm, adminNotes: e.target.value })} disabled={savingManual} />
              </div>
              <PreguntaAviso nombre={usersMap[String(manualModal.userid)]} valor={manualForm.aviso} onChange={(v) => setManualForm((f) => ({ ...f, aviso: v }))} disabled={savingManual} idBase="aviso-manual" />
            </div>
          </>
        )}
      </AdminModal>

      {/* Proof viewer */}
      <AdminModal
        open={!!showProof}
        onClose={() => setShowProof(null)}
        title="Comprobante"
        size="medium"
      >
        {showProof && (
          <div style={{ textAlign: "center" }}>
            {showProof.toLowerCase().includes(".pdf") ? (
              <iframe src={showProof} title="Comprobante PDF" style={{ width: "100%", height: "60vh", border: "none", borderRadius: radius.sm }} />
            ) : (
              <img src={showProof} alt="Comprobante" style={{ maxWidth: "100%", maxHeight: "60vh", borderRadius: radius.sm }} loading="lazy" />
            )}
          </div>
        )}
      </AdminModal>

      {/* A4 — Cobranzas IA */}
      {showCollectionPlan && (
        <div style={styles.overlay} onClick={() => { setShowCollectionPlan(false); setPlanPreview(null); }}>
          <div style={{ ...styles.modal, maxWidth: 980, maxHeight: "85vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ ...styles.modalTitle, display: "inline-flex", alignItems: "center", gap: 8 }}>
                <FaMoneyBillWave color={colors.primary} />
                Cobranzas IA
              </h3>
              <button style={styles.btnIcon} onClick={() => { setShowCollectionPlan(false); setPlanPreview(null); }} aria-label="Cerrar"><FaTimes /></button>
            </div>
            <div style={{ padding: 16 }}>
              <p style={{ fontSize: typography.sizes.sm, color: colors.gray600, marginTop: 0 }}>
                Residentes con saldo vencido ordenados por urgencia. Cada uno cae en una <em>etapa</em> según los días vencidos — el tono del mensaje se adapta a la etapa. Etapa 3 o superior requiere tu confirmación antes de enviar. Pasá el cursor por el chip de etapa o el score para ver el detalle.
              </p>

              {/* Diagnóstico: tester de conexión a Habi */}
              <div style={{ marginBottom: 12, display: "flex", gap: 6, alignItems: "center", fontSize: 12, color: colors.gray600 }}>
                <button
                  type="button"
                  onClick={async () => {
                    setLastError(null);
                    try {
                      const start = Date.now();
                      const resp = await fetch(`${HABI_API_BASE_URL}/health`);
                      const elapsed = Date.now() - start;
                      if (resp.ok) {
                        const data = await resp.json().catch(() => ({}));
                        showToast(`Habi OK (${elapsed}ms) · ${JSON.stringify(data)}`, "success");
                        console.info("[A4] health OK", { elapsed, data });
                      } else {
                        showToast(`Habi respondió ${resp.status}`, "error");
                        setLastError({ context: "health", message: `HTTP ${resp.status}`, status: resp.status });
                      }
                    } catch (e) {
                      showToast(`No se pudo alcanzar Habi: ${e.message || e}`, "error");
                      setLastError({ context: "health", message: e.message || String(e) });
                    }
                  }}
                  style={{ padding: "3px 8px", fontSize: 11, color: colors.gray700, background: "transparent", border: `1px solid ${colors.gray300}`, borderRadius: 6, cursor: "pointer" }}
                >
                  Probar conexión Habi
                </button>
                <span style={{ color: colors.gray500 }}>
                  Backend: <code style={{ fontSize: 11 }}>{HABI_API_BASE_URL}</code>
                </span>
              </div>

              {lastError && (
                <div style={{ padding: 12, background: colors.errorLight, borderRadius: 8, marginBottom: 12, fontSize: 13, color: colors.errorDark }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                    <div>
                      <strong style={{ fontWeight: 400 }}>Último error ({lastError.context}):</strong>{" "}
                      {lastError.message}
                      {lastError.status ? ` · HTTP ${lastError.status}` : ""}
                      <div style={{ fontSize: 11, marginTop: 4, color: colors.gray700 }}>
                        Detalle completo en la consola del navegador (filtrá por <code>[A4]</code>).
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setLastError(null)}
                      style={{ background: "transparent", border: "none", color: colors.errorDark, cursor: "pointer", fontSize: 18, lineHeight: 1 }}
                      aria-label="Cerrar error"
                    >×</button>
                  </div>
                </div>
              )}

              {loadingPlan && (
                <div style={{ padding: 24, textAlign: "center", color: colors.gray600 }}>
                  Cargando plan…
                </div>
              )}

              {!loadingPlan && collectionPlan && collectionPlan.items.length === 0 && (
                <div style={{ padding: 24, textAlign: "center", color: colors.gray600 }}>
                  No hay residentes con saldo vencido. 🎉
                </div>
              )}

              {!loadingPlan && collectionPlan && collectionPlan.items.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {collectionPlan.items.map((item) => {
                    const stageColor =
                      item.stage_suggested >= 4 ? colors.error :
                      item.stage_suggested === 3 ? colors.warning :
                      item.stage_suggested === 2 ? colors.primary : colors.gray600;
                    const isPreviewItem =
                      String(planPreview?.userid || "") === String(item.userid) ||
                      String(pendingPreview?.userid || "") === String(item.userid);
                    const isPendingItem = String(pendingPreview?.userid || "") === String(item.userid);
                    const soloAlquiler = deudaSoloAlquiler.has(String(item.userid));
                    const bloqueo = item.send_blocked_reason || (soloAlquiler ? "es deuda de alquiler (las etapas son de gastos comunes)" : null);
                    return (
                      <React.Fragment key={item.userid}>
                      <div style={{
                        padding: 12,
                        background: isPreviewItem ? colors.primaryLighter : colors.gray50,
                        borderRadius: 8,
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr) auto",
                        gap: 12,
                        alignItems: "center",
                      }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                            <strong style={{ fontSize: typography.sizes.sm, fontWeight: 400, color: colors.gray900 }}>
                              {item.resident_name}
                            </strong>
                            <span style={{ fontSize: 12, color: colors.gray600 }}>Unidad {item.resident_apt}</span>
                            <span
                              title={stageTooltip(item.stage_suggested)}
                              style={{ padding: "2px 10px", fontSize: 11, borderRadius: 9999, background: stageColor, color: colors.white, textTransform: "uppercase", letterSpacing: 0.4, cursor: "help" }}
                            >
                              Etapa {item.stage_suggested}
                            </span>
                            {item.do_not_escalate_flag && (
                              <span style={{ padding: "2px 10px", fontSize: 11, borderRadius: 9999, background: colors.warningLight, color: colors.warning }}>
                                ⚠ Incidencia abierta — no escalar
                              </span>
                            )}
                            {item.requires_human_confirmation && (
                              <span style={{ padding: "2px 10px", fontSize: 11, borderRadius: 9999, background: colors.errorLight, color: colors.error }}>
                                Requiere confirmación humana
                              </span>
                            )}
                            {sentByUserid[String(item.userid)] && (
                              <span
                                title={`Enviado por ${sentByUserid[String(item.userid)].channel === "manual_email" ? "email" : "WhatsApp"} · etapa ${sentByUserid[String(item.userid)].stage}`}
                                style={{ padding: "2px 10px", fontSize: 11, borderRadius: 9999, background: colors.successLight, color: colors.successDark }}
                              >
                                ✓ Enviado hoy
                              </span>
                            )}
                            {soloAlquiler && !item.send_blocked_reason && (
                              <span style={{ padding: "2px 10px", fontSize: 11, borderRadius: 9999, background: colors.gray100, color: colors.gray600 }}>
                                Alquiler del edificio
                              </span>
                            )}
                            {item.send_blocked_reason && (
                              <span
                                title={item.on_hold?.note || "Habi lo registró: no le reclames hasta que lo revises."}
                                style={{ padding: "2px 10px", fontSize: 11, borderRadius: 9999, background: colors.successLight, color: colors.successDark, cursor: "help" }}
                              >
                                {item.on_hold
                                  ? `Avisó que ${item.on_hold.reason === "promesa_pago" ? "paga" : "pagó"} — hasta ${formatDateUY(item.on_hold.until_iso)}`
                                  : "Mandó comprobante por WhatsApp — sin imputar"}
                              </span>
                            )}
                            {item.has_pending_verification && (
                              <span style={{ padding: "2px 10px", fontSize: 11, borderRadius: 9999, background: colors.infoLight, color: colors.infoDark }}>
                                Tiene pago(s) informado(s) para verificar
                              </span>
                            )}
                            {item.active_response_intent && (
                              <span
                                title={item.active_response_text ? `Respondió: "${item.active_response_text}"` : "El residente respondió"}
                                style={{
                                  padding: "2px 10px", fontSize: 11, borderRadius: 9999,
                                  background: colors.infoLight, color: colors.infoDark,
                                  cursor: "help",
                                }}
                              >
                                💬 Respondió: {item.active_response_intent.replace(/_/g, " ")}
                              </span>
                            )}
                          </div>
                          <div style={{ display: "flex", gap: 12, marginTop: 4, fontSize: 12, color: colors.gray700, flexWrap: "wrap" }}>
                            <span>{formatCurrencyUY(item.total_amount)} pendiente</span>
                            <span>{item.days_overdue} días vencido</span>
                            <span>{item.open_payment_count} pago{item.open_payment_count !== 1 ? "s" : ""} abierto{item.open_payment_count !== 1 ? "s" : ""}</span>
                            <span title={scoreTooltip(item)} style={{ color: colors.gray600, cursor: "help", borderBottom: `1px dotted ${colors.gray400}` }}>
                              Score: {item.score}
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            console.info("[A4] click Generar mensaje", { userid: item.userid, stage: item.stage_suggested, building_id: buildingId });
                            fetchCollectionPreview(item.userid, item.stage_suggested);
                          }}
                          disabled={isPendingItem || !!bloqueo}
                          title={bloqueo ? `No se le reclama: ${bloqueo}` : undefined}
                          style={{
                            padding: "6px 14px",
                            fontSize: typography.sizes.sm,
                            fontWeight: 400,
                            color: isPendingItem || bloqueo ? colors.gray600 : colors.white,
                            background: isPendingItem || bloqueo ? colors.gray100 : colors.primary,
                            border: "none",
                            borderRadius: 8,
                            cursor: isPendingItem || bloqueo ? "not-allowed" : "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          {isPendingItem && (
                            <span style={{
                              display: "inline-block",
                              width: 12,
                              height: 12,
                              border: `2px solid ${colors.gray400}`,
                              borderTopColor: colors.primary,
                              borderRadius: "50%",
                              animation: "spin 0.8s linear infinite",
                            }} />
                          )}
                          {isPendingItem ? "Generando…" : "Generar mensaje"}
                        </button>
                      </div>

                      {/* Preview inline — aparece pegado al residente clickeado */}
                      {isPreviewItem && (
                        <div style={{
                          marginTop: 4,
                          padding: 16,
                          background: colors.primarySubtle,
                          borderLeft: `3px solid ${colors.primary}`,
                          borderRadius: 8,
                          display: "flex",
                          flexDirection: "column",
                          gap: 10,
                        }}>
                  {/* Skeleton mientras genera (sin planPreview todavia) */}
                  {isPendingItem && !planPreview && (
                    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 12, color: colors.gray700 }}>
                      <span style={{
                        display: "inline-block", width: 18, height: 18,
                        border: `3px solid ${colors.gray300}`,
                        borderTopColor: colors.primary,
                        borderRadius: "50%",
                        animation: "spin 0.8s linear infinite",
                      }} />
                      <div>
                        <div style={{ fontSize: 14, color: colors.gray900 }}>
                          Generando mensaje con IA…
                        </div>
                        <div style={{ fontSize: 12, color: colors.gray600 }}>
                          Habitia está armando el texto adaptado a la etapa {pendingPreview?.stage}. Puede tardar 5-10 segundos.
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Header del preview cuando ya hay data */}
                  {planPreview && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <strong style={{ fontSize: typography.sizes.sm, color: colors.primary, fontWeight: 400 }}>
                      Mensaje sugerido — {planPreview.resident_name} (Etapa {planPreview.stage})
                    </strong>
                    <button
                      type="button"
                      onClick={() => { setPlanPreview(null); setLegalLetter(null); }}
                      style={{ background: "transparent", border: "none", color: colors.gray600, cursor: "pointer", fontSize: 13 }}
                    >
                      Cerrar
                    </button>
                  </div>
                  )}

                  {planPreview?.derive_to_human && (
                    <div style={{ padding: 12, background: colors.errorLight, color: colors.error, borderRadius: 8, fontSize: 13 }}>
                      {planPreview.reason}
                    </div>
                  )}

                  {planPreview && !planPreview.derive_to_human && planPreview.message && (
                    <>
                      {planPreview.requires_human_confirmation && (
                        <div style={{ padding: 10, background: colors.warningLight, color: colors.warningDark || colors.warning, borderRadius: 8, fontSize: 12 }}>
                          ⚠ Etapa {planPreview.stage}: este mensaje requiere revisión humana cuidadosa antes de enviar. El próximo paso luego de este recordatorio es intimación formal / carta-documento.
                        </div>
                      )}

                      {/* Sprint 3 — indicador de canal de envío */}
                      {(() => {
                        const av = templateAvailability[planPreview.stage];
                        if (av?.approved && av?.enabledForBuilding) {
                          return (
                            <div style={{ padding: 10, background: colors.successLight, color: colors.successDark, borderRadius: 8, fontSize: 12, display: "flex", alignItems: "center", gap: 8 }}>
                              <FaCheck size={12} />
                              <div>
                                <strong style={{ fontWeight: 400 }}>Envío 100% automático disponible:</strong> usaremos el template aprobado <code>{av.templateKey}</code> (sin ventana 24h, respeta preferencias del residente).
                              </div>
                            </div>
                          );
                        }
                        if (av?.approved && !av?.enabledForBuilding) {
                          return (
                            <div style={{ padding: 10, background: colors.warningLight, color: colors.warningDark, borderRadius: 8, fontSize: 12 }}>
                              ⚠ Hay un template aprobado para esta etapa, pero NO está habilitado en este edificio. Habilitalo en <strong style={{ fontWeight: 400 }}>Editar Edificio → Comunicaciones</strong> para usar envío automático.
                            </div>
                          );
                        }
                        return (
                          <div style={{ padding: 10, background: colors.infoLight, color: colors.infoDark, borderRadius: 8, fontSize: 12 }}>
                            ℹ Aún no hay template aprobado por Meta para esta etapa. El botón <strong style={{ fontWeight: 400 }}>"Enviar por WhatsApp"</strong> abrirá WhatsApp Web con el mensaje precargado (1 click para enviar).
                          </div>
                        );
                      })()}

                      <div>
                        <div style={{ fontSize: 12, color: colors.gray600, marginBottom: 4, display: "flex", justifyContent: "space-between" }}>
                          <span>WhatsApp ({(editedWhatsapp || "").length} chars) · editable</span>
                          {editedWhatsapp !== (planPreview.message.whatsapp_text || "") && (
                            <button
                              type="button"
                              onClick={() => setEditedWhatsapp(planPreview.message.whatsapp_text || "")}
                              style={{ fontSize: 11, color: colors.gray700, background: "transparent", border: "none", cursor: "pointer", textDecoration: "underline" }}
                            >
                              ↺ revertir al original
                            </button>
                          )}
                        </div>
                        <textarea
                          value={editedWhatsapp}
                          onChange={(e) => setEditedWhatsapp(e.target.value)}
                          rows={6}
                          style={{
                            width: "100%",
                            padding: 10,
                            background: colors.white,
                            border: `1px solid ${colors.gray200}`,
                            borderRadius: 6,
                            fontSize: 13,
                            color: colors.gray900,
                            fontFamily: "inherit",
                            resize: "vertical",
                            boxSizing: "border-box",
                          }}
                        />
                        <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <button
                            type="button"
                            onClick={handleSendWhatsAppDirect}
                            disabled={sendingDirect === "whatsapp" || !editedWhatsapp.trim()}
                            title="Intenta envío directo via Meta Cloud API. Si la ventana de 24h está cerrada, abre WhatsApp Web con el mensaje precargado."
                            style={{ padding: "5px 12px", fontSize: 11, color: colors.white, background: colors.primary, border: "none", borderRadius: 8, cursor: sendingDirect === "whatsapp" ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}
                          >
                            {sendingDirect === "whatsapp" && (
                              <span style={{ display: "inline-block", width: 10, height: 10, border: `2px solid ${colors.white}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                            )}
                            {sendingDirect === "whatsapp" ? "Enviando…" : "Enviar por WhatsApp"}
                          </button>
                          <button
                            type="button"
                            onClick={handleOpenWaMe}
                            disabled={!editedWhatsapp.trim()}
                            title="Abre WhatsApp Web/App con el mensaje precargado para enviar con un click."
                            style={{ padding: "5px 12px", fontSize: 11, color: colors.primary, background: "transparent", border: `1px solid ${colors.primary}`, borderRadius: 8, cursor: !editedWhatsapp.trim() ? "not-allowed" : "pointer" }}
                          >
                            Abrir wa.me
                          </button>
                          <button
                            type="button"
                            onClick={() => copyToClipboard(editedWhatsapp, "WhatsApp")}
                            style={{ padding: "5px 12px", fontSize: 11, color: colors.gray700, background: "transparent", border: `1px solid ${colors.gray300}`, borderRadius: 8, cursor: "pointer" }}
                          >
                            Copiar
                          </button>
                        </div>
                      </div>

                      <div>
                        <div style={{ fontSize: 12, color: colors.gray600, marginBottom: 4 }}>Email — asunto: {planPreview.message.subject}</div>
                        <div style={{ padding: 10, background: colors.white, borderRadius: 6, fontSize: 13, color: colors.gray900, maxHeight: 200, overflowY: "auto" }}
                             dangerouslySetInnerHTML={{ __html: planPreview.message.email_html || "(sin contenido)" }} />
                        <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                          <button
                            type="button"
                            onClick={() => copyToClipboard(planPreview.message.email_html, "Email HTML")}
                            style={{ padding: "4px 10px", fontSize: 11, color: colors.primary, background: "transparent", border: `1px solid ${colors.primary}`, borderRadius: 8, cursor: "pointer" }}
                          >
                            Copiar HTML
                          </button>
                          <button
                            type="button"
                            onClick={() => logCollectionSent("manual_email")}
                            disabled={sendingLog?.channel === "manual_email"}
                            title="Registra que ya enviaste el email manualmente."
                            style={{ padding: "4px 10px", fontSize: 11, color: colors.white, background: colors.primary, border: "none", borderRadius: 8, cursor: sendingLog?.channel === "manual_email" ? "not-allowed" : "pointer" }}
                          >
                            {sendingLog?.channel === "manual_email" ? "Registrando…" : "Marcar como enviado por email"}
                          </button>
                        </div>
                      </div>

                      <div style={{ fontSize: 12, color: colors.gray600 }}>
                        Tono: <strong style={{ fontWeight: 400, color: colors.gray900 }}>{planPreview.message.tone_used}</strong>
                        {planPreview.message.key_facts?.length > 0 && (
                          <> · Datos incluidos: {planPreview.message.key_facts.join(", ")}</>
                        )}
                      </div>

                      {/* C2 + Sprint 5 — Escalar a carta-documento / derivar a gestor legal (etapa 4+) */}
                      {planPreview.stage >= 4 && (
                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${colors.gray200}`, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                          <button
                            type="button"
                            onClick={handleGenerateLetter}
                            disabled={generatingLetter}
                            style={{ ...btnDanger, padding: "8px 16px", fontSize: 12, opacity: generatingLetter ? 0.6 : 1 }}
                          >
                            {generatingLetter ? "Generando…" : "📜 Generar carta-documento"}
                          </button>
                          <button
                            type="button"
                            onClick={handleDeriveLegal}
                            disabled={derivingLegal}
                            title="Crea un caso en legal_handoff y notifica al admin por email para gestión externa."
                            style={{ ...btnDanger, padding: "8px 16px", fontSize: 12, opacity: derivingLegal ? 0.6 : 1 }}
                          >
                            {derivingLegal ? "Derivando…" : "⚖ Derivar a gestor legal"}
                          </button>
                          <span style={{ fontSize: 11, color: colors.gray600 }}>
                            Para envío por correo certificado o gestión externa.
                          </span>
                          {legalHandoff && (
                            <div style={{ width: "100%", marginTop: 8, padding: 10, background: colors.successLight, color: colors.successDark, borderRadius: 8, fontSize: 12 }}>
                              ✓ Derivación registrada con id <code>{legalHandoff.handoff_id}</code>
                              {legalHandoff.admin_notified && " · admin notificado por email"}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {/* C2 — Si la etapa es 5 (derive_to_human), igual ofrecer carta */}
                  {planPreview?.derive_to_human && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${colors.gray200}` }}>
                      <button
                        type="button"
                        onClick={handleGenerateLetter}
                        disabled={generatingLetter}
                        style={{ ...btnDanger, padding: "8px 16px", fontSize: 12 }}
                      >
                        {generatingLetter ? "Generando…" : "📜 Generar carta-documento (derivación legal)"}
                      </button>
                    </div>
                  )}

                  {/* C2 — Carta generada */}
                  {legalLetter && (
                    <div style={{ marginTop: 16, padding: 14, background: colors.gray50, borderRadius: 8 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                        <strong style={{ fontWeight: 400, fontSize: 13, color: colors.gray900 }}>
                          📜 Carta-documento generada
                        </strong>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(legalLetter.markdown, "Carta-documento")}
                          style={{ padding: "4px 12px", fontSize: 11, color: colors.primary, background: "transparent", border: `1px solid ${colors.primary}`, borderRadius: 8, cursor: "pointer" }}
                        >
                          Copiar markdown
                        </button>
                      </div>
                      <div style={{ padding: 12, background: colors.white, borderRadius: 6, fontSize: 12, color: colors.gray900, whiteSpace: "pre-wrap", maxHeight: 300, overflowY: "auto", fontFamily: typography.fontFamily }}>
                        {legalLetter.markdown}
                      </div>
                      {legalLetter.checklist_envio && legalLetter.checklist_envio.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                          <strong style={{ fontSize: 11, color: colors.gray700, fontWeight: 400 }}>Checklist de envío:</strong>
                          <ul style={{ margin: "4px 0 0", paddingLeft: 18, fontSize: 11, color: colors.gray700 }}>
                            {legalLetter.checklist_envio.map((c, i) => <li key={i}>{c}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Fallback: backend respondió OK pero ni message ni derive_to_human */}
                  {planPreview && !planPreview.message && !planPreview.derive_to_human && (
                    <div style={{ padding: 12, background: colors.warningLight, color: colors.warningDark, borderRadius: 8, fontSize: 12 }}>
                      El backend respondió pero no devolvió ni mensaje ni indicación de derivar. Esto puede indicar un bug en <code>/api/collection-plan/preview-message</code>. Mirá la consola (filtrá por <code>[A4]</code>) para ver el JSON completo de la respuesta.
                    </div>
                  )}
                        </div>
                      )}
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  function renderPayments(paymentsList) {
    // El saldo a favor se aplica de forma REAL y ATÓMICA en el cierre
    // (computeCreditApplications): el período cubierto queda con `paidFromCredit` y su
    // pago pagado. Un período saldado NO ofrece "Pago manual" (evita pagarlo dos
    // veces); uno cubierto en parte sí, por lo que falta. Montos en pesos enteros.
    return (
      <div style={styles.payTable}>
        <div style={styles.payTableHead}>
          <span>Período</span>
          <span>Vencimiento</span>
          <span>Monto</span>
          <span>Desglose</span>
          <span>Estado</span>
          <span>Acciones</span>
        </div>
        {paymentsList.map((p) => {
          const paid = Number(p.paidAmount) || 0;
          // remainingAmount != null se respeta (puede ser negativo = crédito a favor);
          // solo si viene ausente se deriva de amount - paid. (Antes `|| Number(amount)`
          // hacía que un remaining exactamente 0 cayera al amount completo.) En pesos.
          const restoPesos = saldoEnPesos(p);
          // Cubierto con saldo a favor (real, materializado por el cierre en paidFromCredit).
          const paidCredit = roundPesos(p.paidFromCredit);
          const isCoveredByCredit = paidCredit > 0;        // tiene crédito aplicado
          const isFullyByCredit = isCoveredByCredit && p.paid; // saldado por crédito
          const shownCoverage = paidCredit;
          const shownRest = paidCredit > 0 && !p.paid ? restoPesos : 0;
          return (
            <div key={p.id} style={styles.payTableRow}>
              <span style={{ fontWeight: 400, fontSize: 13 }}>{getMonthName(p.month)} {p.year}</span>
              <span style={{ fontSize: typography.sizes.sm, color: colors.gray900 }}>{formatDateUY(p.maturity)}</span>
              <span style={{ fontSize: 13 }}>
                {formatCurrencyUY(p.amount)}
                {paid > 0 && !p.paid && restoPesos > 0 && (
                  <span style={{ display: "block", fontSize: typography.sizes.xs, color: colors.warning }}>
                    Pagado: {formatCurrencyUY(paid)} / Resta: {formatCurrencyUY(restoPesos)}
                  </span>
                )}
                {shownCoverage > 0 && (
                  <span style={{ display: "block", fontSize: typography.sizes.xs, color: colors.success }}>
                    Cubierto con saldo a favor: {formatCurrencyUY(shownCoverage)}
                    {shownRest > 0 ? ` · Resta: ${formatCurrencyUY(shownRest)}` : ""}
                  </span>
                )}
                {restoPesos < 0 && (
                  <span style={{ display: "block", fontSize: typography.sizes.xs, color: colors.success }}>
                    Saldo a favor: {formatCurrencyUY(Math.abs(restoPesos))}
                  </span>
                )}
                {p.status === "pendiente_verificacion" && p.montoNoCierra && (
                  <span style={{ display: "block", fontSize: typography.sizes.xs, color: colors.errorDark }}>
                    El comprobante ({formatCurrencyUY(p.montoComprobante)}) no cierra con lo que debía ({formatCurrencyUY(p.deudaAlInformar)})
                  </span>
                )}
              </span>
              <span style={{ fontSize: typography.sizes.xs, color: colors.gray600 }}>
                {p.breakdown ? (
                  <>
                    {getBreakdownRows(p.breakdown).map((row) => (
                      <div key={row.key}>{row.label}: {formatCurrencyUY(row.value)}</div>
                    ))}
                  </>
                ) : "—"}
              </span>
              <span>
                {isFullyByCredit ? (
                  <span style={{
                    fontSize: typography.sizes.xs, color: colors.success, fontWeight: 400,
                    background: colors.successLight, padding: "2px 8px", borderRadius: 10, whiteSpace: "nowrap",
                  }}>
                    Pagado (saldo a favor)
                  </span>
                ) : (
                  <StatusBadgeShared status={p.status || (p.paid ? "aprobado" : "pendiente")} size="sm" />
                )}
              </span>
              <span style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {p.status === "pendiente_verificacion" && (
                  <button style={styles.btnSmall} onClick={() => { setVerifyModal(p); setRejectReason(""); setAvisoVerificacion(null); }}>
                    <FaEye size={10} /> Verificar
                  </button>
                )}
                {(!p.paid && p.status !== "pendiente_verificacion") && (
                  <button style={{ ...styles.btnSmall, color: colors.primary, borderColor: colors.primaryLight }} onClick={() => { setManualModal(p); setManualForm(formPagoManual(String(Math.max(0, restoPesos)))); }}>
                    <FaMoneyBillWave size={10} /> Pago manual
                  </button>
                )}
                {(p.paymentProof || p.comprobanteUrl) && (
                  <button style={{ ...styles.btnSmall, color: colors.gray600, borderColor: colors.gray200 }} onClick={() => setShowProof(p.paymentProof || p.comprobanteUrl)}>
                    <FaFileImage size={10} />
                  </button>
                )}
              </span>
            </div>
          );
        })}
      </div>
    );
  }
};

/**
 * Lista de pagos de un ocupante con colapso: siempre muestra los IMPAGOS y el
 * registro más reciente (la lista viene ordenada del más nuevo al más viejo);
 * los pagos anteriores ya saldados quedan detrás de "Ver más".
 */
function CollapsiblePayments({ payments, render }) {
  const [verMas, setVerMas] = useState(false);
  const visibles = useMemo(
    () => (payments || []).filter((p, i) => i === 0 || !p.paid),
    [payments]
  );
  const ocultos = (payments || []).length - visibles.length;

  if (ocultos <= 0) return render(payments || [], payments || []);
  return (
    <>
      {render(verMas ? payments : visibles, payments)}
      <button type="button" onClick={() => setVerMas((v) => !v)} style={styles.verMasBtn}>
        {verMas ? (
          <><FaChevronUp size={10} /> Ver menos</>
        ) : (
          <><FaChevronDown size={10} /> Ver {ocultos} {ocultos === 1 ? "período anterior" : "períodos anteriores"}</>
        )}
      </button>
    </>
  );
}

// Estilos migrados a design tokens
const styles = {
  container: {
    padding: spacing[6],
    fontFamily: typography.fontFamily,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: spacing[3],
  },
  title: {
    ...pageTitle,
    display: "flex", alignItems: "center",
  },
  subtitle: { fontSize: typography.sizes.base, color: colors.gray600, margin: "4px 0 0" },
  legalBanner: {
    display: "flex", alignItems: "center", gap: spacing[2],
    padding: "10px 14px", background: colors.primarySubtle, border: `1px solid ${colors.primaryLight}`,
    borderRadius: radius.sm, fontSize: typography.sizes.sm, color: colors.infoDark, marginBottom: spacing[4], lineHeight: 1.5,
  },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: spacing[4],
    marginBottom: spacing[4],
  },
  tooltipWrap: {
    position: "absolute",
    top: spacing[2],
    right: spacing[2],
  },
  agingRow: {
    ...cardBase,
    padding: spacing[4],
    marginBottom: spacing[4],
  },
  agingHeader: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    marginBottom: spacing[3],
  },
  agingSectionTitle: {
    fontSize: typography.sizes.sm,
    color: colors.gray600,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    fontWeight: 400,
  },
  agingBuckets: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: spacing[2],
  },
  agingBucket: {
    ...cardBase,
    padding: spacing[3],
    borderTop: "3px solid transparent",
    textAlign: "center",
    boxShadow: shadows.xs,
  },
  agingBucketLabel: {
    fontSize: typography.sizes.xs,
    color: colors.gray600,
    display: "block",
    marginBottom: spacing[1],
    fontWeight: 400,
  },
  agingBucketValue: {
    fontSize: typography.sizes.md,
    color: colors.black,
    fontWeight: 400,
  },
  filterRow: {
    display: "flex", gap: spacing[3], marginBottom: 14, flexWrap: "wrap", alignItems: "center",
  },
  searchBox: {
    display: "flex", alignItems: "center", gap: spacing[2],
    background: colors.white, border: `1px solid ${colors.gray200}`, borderRadius: radius.sm,
    padding: "6px 12px", flex: "1 1 200px", maxWidth: 300,
  },
  searchInput: {
    border: "none", outline: "none", fontSize: typography.sizes.base, flex: 1, background: "transparent",
    fontFamily: typography.fontFamily,
  },
  filterTabs: { display: "flex", gap: 4, flexWrap: "wrap" },
  filterTab: {
    padding: "6px 12px", borderRadius: radius.sm, border: `1px solid ${colors.gray200}`,
    background: colors.white, fontSize: typography.sizes.sm, fontWeight: 400, color: colors.gray900, cursor: "pointer",
    transition: `all ${transitions.fast}`,
  },
  filterTabActive: { background: colors.primary, color: colors.white, borderColor: colors.primary },
  list: { display: "flex", flexDirection: "column", gap: spacing[2] },
  unitCard: {
    background: colors.white, borderRadius: radius.md,
    boxShadow: shadows.sm, overflow: "hidden",
  },
  unitHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "12px 16px", cursor: "pointer", gap: spacing[3],
  },
  unitLeft: { display: "flex", alignItems: "center", gap: spacing[2], minWidth: 0 },
  unitNumber: { fontSize: typography.sizes.md, fontWeight: 400, color: colors.black },
  // Badge de verificación — color púrpura sin token exacto, se mantiene
  verifBadge: {
    fontSize: 10, fontWeight: 400, color: "#8b5cf6", background: "#f5f3ff",
    borderRadius: radius.md, padding: "2px 8px",
  },
  unitRight: { display: "flex", alignItems: "center", gap: spacing[5] },
  unitUserCol: {
    display: "flex", flexDirection: "column", gap: 2, textAlign: "right", minWidth: 0,
  },
  unitUserLabel: {
    fontSize: typography.sizes.sm, color: colors.gray600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 200,
  },
  unitDebtCol: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, minWidth: 100 },
  debtAmount: { fontSize: typography.sizes.md, fontWeight: 400 },
  overdueBadge: {
    fontSize: 10, fontWeight: 400, color: colors.error, background: colors.errorLight,
    borderRadius: radius.md, padding: "1px 8px",
  },
  unitBody: { padding: "0 16px 16px", borderTop: `1px solid ${colors.gray100}` },
  // Bloque por ocupante: contenedor propio con acento de color por rol en el
  // borde izquierdo (Propietario azul / Inquilino celeste / Saliente gris) para
  // que las tablas de cada persona no se confundan entre sí.
  roleSection: {
    marginTop: spacing[3],
    background: colors.gray50,
    border: `1px solid ${colors.gray200}`,
    borderLeft: `3px solid ${colors.gray300}`,
    borderRadius: radius.md,
    padding: `${spacing[2]}px ${spacing[3]}px ${spacing[3]}px`,
  },
  roleHeader: {
    display: "flex", alignItems: "center", gap: spacing[2],
    padding: "6px 0", marginBottom: 4,
  },
  rolePill: {
    fontSize: 11, fontWeight: 400, textTransform: "uppercase", letterSpacing: "0.5px",
    padding: "2px 9px", borderRadius: 999, flexShrink: 0,
  },
  roleName: { fontSize: typography.sizes.base, color: colors.black },
  verMasBtn: {
    display: "inline-flex", alignItems: "center", gap: 6,
    marginTop: spacing[2], padding: "6px 0",
    background: "transparent", border: "none", cursor: "pointer",
    fontSize: typography.sizes.sm, fontFamily: "inherit", color: colors.primary,
  },
  payTable: { fontSize: typography.sizes.sm },
  payTableHead: {
    display: "grid", gridTemplateColumns: "100px 90px 120px 1fr 120px 140px",
    gap: spacing[2], padding: "6px 0", fontWeight: 400, color: colors.gray600,
    borderBottom: `1px solid ${colors.gray100}`, fontSize: typography.sizes.xs,
  },
  payTableRow: {
    display: "grid", gridTemplateColumns: "100px 90px 120px 1fr 120px 140px",
    gap: spacing[2], padding: "8px 0", borderBottom: `1px solid ${colors.gray100}`, alignItems: "start",
  },
  // Overlay y modal usando tokens del design system
  overlay: {
    ...modalOverlay,
  },
  modal: {
    background: colors.white, borderRadius: radius.md, width: "90%", maxWidth: 520,
    maxHeight: "90vh", overflow: "auto", padding: spacing[6],
    boxShadow: shadows.modal,
  },
  modalHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: spacing[3],
  },
  modalTitle: { fontSize: typography.sizes.xl, fontWeight: 400, color: colors.black, margin: 0 },
  label: { fontSize: typography.sizes.sm, fontWeight: 400, color: colors.gray900, marginBottom: 4, display: "block" },
  fileLabel: {
    display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0,
    padding: "8px 12px", fontSize: typography.sizes.sm, color: colors.gray900,
    background: colors.gray50, border: `1px solid ${colors.gray300}`, borderRadius: radius.sm,
    cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  input: {
    ...inputBase,
    padding: "8px 12px",
    fontSize: typography.sizes.base,
    boxSizing: "border-box",
  },
  // Botones usando spreads del design system con overrides de tamaño
  btnPrimary: {
    ...btnPrimary,
    display: "flex",
    padding: "8px 18px",
    fontSize: typography.sizes.base,
  },
  btnSecondary: {
    ...btnSecondary,
    display: "flex",
    padding: "8px 18px",
    background: colors.gray100,
    fontSize: typography.sizes.base,
  },
  btnSuccess: {
    ...btnPrimary,
    display: "flex",
    padding: "8px 16px",
    background: colors.success,
    fontSize: typography.sizes.base,
  },
  btnDanger: {
    ...btnDanger,
    display: "flex",
    padding: "8px 16px",
    background: colors.white, color: colors.error,
    border: "1px solid #fecaca",
    fontSize: typography.sizes.base,
  },
  btnIcon: {
    ...btnGhost,
    padding: 4, fontSize: 16,
  },
  // Botón pequeño — color púrpura para verificación, sin token exacto
  btnSmall: {
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "3px 8px", fontSize: typography.sizes.xs, fontWeight: 400, borderRadius: 6,
    border: "1px solid #c4b5fd", color: "#8b5cf6", background: colors.white, cursor: "pointer",
    transition: `all ${transitions.fast}`,
  },
};

export default CobranzaAdmin;
